import React, { useState, useEffect, useCallback } from 'react';
import { agentApi, tshareUrl } from '../../services/api';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';
import ChatBox, { mergeIncoming } from '../../components/ChatBox';
import ReportIssue from '../../components/ReportIssue';
import { Lightbox } from '../../components/Lightbox';
import ManualActivities from './ManualActivities';

const PAY_LABEL = { stripe: '信用卡', wxpay: '微信', alipay: '支付宝', offline: '线下' };
const ORDER_STATE = { paid: ['已到账', '#16a34a'], refunded: ['已退款', '#7c3aed'], cancelled: ['已取消', '#9ca3af'], pending: ['待付款', '#b45309'] };
const TODO_LABEL = {
  blogger_flag: '博主那边', merchant_flag: '商家那边', draft: '等审稿',
  objected: '商家有异议', unmatched_shop: '认不出是哪家店', feedback: '运营报的问题',
  outage: '外部服务挂了', blogger_pending: '新博主等审核',
};
const TODO_TONE = {
  blogger_flag: '#FA1C51', merchant_flag: '#b45309', draft: '#2563eb',
  objected: '#dc2626', unmatched_shop: '#7c3aed', feedback: '#0f766e',
  outage: '#991b1b', blogger_pending: '#7c3aed',
};

// 待办要的是「压了多久」，不是精确时间 —— 压了三天的那条得一眼看出来
function timeAgo(v) {
  const ms = Date.now() - new Date(v).getTime();
  if (ms < 0 || Number.isNaN(ms)) return '';
  const h = Math.floor(ms / 3600000);
  if (h < 1) return '刚刚';
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

const NAV_LABEL = { todo: '待办', deals: '进行中', drafts: '审稿', bloggers: '博主', shops: '店铺', feedback: '反馈' };
// 画像字段的中文名。运营看的是「常去哪」，不是 areas
const SIG_FIELD = {
  areas: '常去区域', cuisines: '口味', dislikes: '忌口', availability: '有空时段',
  maxTravelMinutes: '愿意跑多远', hasCar: '有没有车', wantsPaid: '要不要现金',
  minMealValue: '最低套餐', platforms: '其他平台', audienceNote: '粉丝画像', fact: '聊出来的一句话', learned: '补充说明',
};
const FB_STATUS = { new: '待看', triaged: '看过了', fixed: '已修', wontfix: '不改' };

const DEAL_LABEL = {
  matched: '待邀请', invited: '已邀请', negotiating: '谈判中',
  agreed: '已谈成', declined: '已拒绝', expired: '无回应', committed: '已落库',
};
// 状态得能扫一眼分清「成了 / 还在谈 / 黄了」，三类三个颜色，别都是粉的
const DEAL_TONE = {
  matched:     { color: '#2563eb', background: '#eff6ff' },
  invited:     { color: '#2563eb', background: '#eff6ff' },
  negotiating: { color: '#b45309', background: '#fffbeb' },
  agreed:      { color: '#16a34a', background: '#f0fdf4' },
  committed:   { color: '#16a34a', background: '#f0fdf4' },
  declined:    { color: '#9ca3af', background: '#f3f4f6' },
  expired:     { color: '#9ca3af', background: '#f3f4f6' },
};

// 撮合运营台。
//
// 结构按「运营一天怎么干活」排，不按数据表排：
//   待办   —— 所有店加起来要人管的事，进来先看这个，跟哪家店无关
//   进行中 —— 所有在谈/已谈成的单子，一张总表，需要时再按店或状态筛
//   审稿   —— 稿子队列，本来就跨店
//   博主   —— 全部博主画像，本来就跨店
//   店铺   —— 唯一真正按店走的地方：挑人发邀请、看这家店的条件和名额
//
// 之前是顶上一个活动下拉管着全部五个页面，但只有「挑人发邀请」是真按店的。
// 结果看待办、看稿子、看博主都要先选一家店，选错了就看不见东西。
// 只有五类真的选项 + 一个出口。用运营自己的说法，不用我们的术语。
const WHY_TAGS = [
  ['wrong', '它搞错了（人 / 单子 / 时间 / 数字）'],
  ['missed', '它漏了该做的'],
  ['extra', '它多事了'],
  ['made_up', '它瞎说（说了不存在的事）'],
  ['tone', '话说得不合适'],
  ['unsure', '说不清'],
];

export default function AgentConsole() {
  const [view, setView] = useState('todo');      // todo | deals | drafts | bloggers | shops | orders
  const [orders, setOrders] = useState({ rows: [], total: 0, summary: {} });
  const [ordersPage, setOrdersPage] = useState(1);
  const [shopOrders, setShopOrders] = useState({ rows: [], total: 0, summary: {} });
  const [shopId, setShopId] = useState('');      // 进到某家店才有值（= activityId）
  const [convId, setConvId] = useState('');      // 有值就盖一层对话面板
  const [backTo, setBackTo] = useState('todo');  // 从哪来的，关掉对话回哪去
  const [convLabel, setConvLabel] = useState(''); // 现在看的是谁那一边，标题里写清楚
  const [detail, setDetail] = useState(null);    // 打开的那张单子的全貌
  const [detailFrom, setDetailFrom] = useState('deals');

  const [activities, setActivities] = useState([]);
  const [match, setMatch] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [deals, setDeals] = useState([]);
  const [dealFilter, setDealFilter] = useState('live');   // live | all | agreed
  const [talkingShops, setTalkingShops] = useState([]);   // 商家在聊、还没有单子的店
  const [dealQ, setDealQ] = useState('');        // 单子搜索：博主名/电话/店名
  const [bloggerQ, setBloggerQ] = useState('');  // 博主搜索：名字/电话/小红书号
  const [sigEvents, setSigEvents] = useState(null);   // 画像待审
  const [sigTab, setSigTab] = useState('');           // '' = 不看；pending/active

  // 每一栏各记各的页码。线上一个来回 1.5 秒，一次只取 20 条，翻页才再取
  const [todoPage, setTodoPage] = useState(1);
  const [dealPage, setDealPage] = useState(1);
  const [shopPage, setShopPage] = useState(1);
  const [bloggerPage, setBloggerPage] = useState(1);
  const [draftPage, setDraftPage] = useState(1);
  // 审稿两个队列：manual = 人工活动的稿子，等人通过才能发（挡着博主）；todo = 已经能发的，事后回看
  const [draftQueue, setDraftQueue] = useState('manual');
  const [manualDraftTotal, setManualDraftTotal] = useState(0);
  const [fbPage, setFbPage] = useState(1);
  const [dealTotal, setDealTotal] = useState(0);
  const [actTotal, setActTotal] = useState(0);
  const [draftTotal, setDraftTotal] = useState(0);
  const [fbTotal, setFbTotal] = useState(0);
  const [loading, setLoading] = useState('');   // 哪一栏在加载，页面上要有个交代
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  // 运营每改一次助理做错的地方，顺手问一句「哪儿不对」。
  // 只有六个按钮、点一下就走 —— 他是在处理工单的间隙点这一下，
  // 不是在给我们做标注。多一个字他都不会填。
  const [why, setWhy] = useState(null);       // { title, resolve }
  const askWhy = (title) => new Promise((resolve) => setWhy({ title, resolve }));
  const [profileData, setProfileData] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [todo, setTodo] = useState({ items: [], byActivity: {}, total: 0 });
  const [drill, setDrill] = useState(null);      // 演练模式的三个开关
  const [feedback, setFeedback] = useState([]);
  const [fbFilter, setFbFilter] = useState('new');

  useEffect(() => {
    agentApi.status().then((d) => setDrill(d.drill || null)).catch(() => {});
  }, []);

  // 按**商家**列，不按活动容器列 —— 容器是条件谈齐时才自建的，
  // 按它列会让「开通了但还没谈起来」的店整个从界面上消失
  const loadActivities = useCallback(() => {
    setLoading('shops');
    agentApi.shops({ page: shopPage })
      .then((r) => { setActivities(r.list || []); setActTotal(r.total || 0); })
      .catch((e) => setMsg(e?.response?.data?.error || 'agent 接口没开：后端 .env 加 AGENT_MVP=true 再重启'))
      .finally(() => setLoading(''));
  }, [shopPage]);
  useEffect(() => { loadActivities(); }, [loadActivities]);

  // 购买流水。运营要的是「哪家店买了啥」—— 名额在商家点购买那一下就发了，
  // 这里不挡任何人，纯账本。带 merchantId 就只看一家店（店铺详情里那一块用）。
  const loadOrders = useCallback((merchantId) => {
    agentApi.subOrders({ page: merchantId ? 1 : ordersPage, merchantId: merchantId || undefined })
      .then((r) => (merchantId ? setShopOrders(r) : setOrders(r)))
      .catch(() => {});
  }, [ordersPage]);

  // 待办跟店无关，进来就拉，之后自己刷 —— 运营不该靠记得点某家店才发现出事
  const loadTodo = useCallback(() => {
    agentApi.todo({ page: todoPage }).then(setTodo).catch(() => {});
  }, [todoPage]);
  useEffect(() => {
    loadTodo();
    const t = setInterval(loadTodo, 30000);
    return () => clearInterval(t);
  }, [loadTodo]);

  // 撮合单一张总表。进某家店时才按店收窄
  // 筛选和搜索都交给后端。前端 filter 看着简单，但后端是「最近 200 条」封顶的，
  // 单子多了之后截断发生在筛选之前 —— 页面会显示「没有符合的撮合单」，其实只是没在那 200 条里
  const DEAL_STATUS = { live: 'invited,negotiating,agreed', agreed: 'agreed,committed', all: '' };
  const loadDeals = useCallback(() => {
    const params = {};
    if (shopId) params.activityId = shopId;
    if (DEAL_STATUS[dealFilter]) params.status = DEAL_STATUS[dealFilter];
    if (dealQ.trim()) params.q = dealQ.trim();
    params.page = dealPage;
    // 在谈这一栏也要看到「商家在跟助理聊、还没发邀请」的店，不然前期一片空白
    if (dealFilter === 'live' && !shopId) params.withShops = '1';
    setLoading('deals');
    agentApi.deals(params)
      .then((r) => { setDeals(r.list || []); setDealTotal(r.total || 0); setTalkingShops(r.talkingShops || []); })
      .catch(() => {})
      .finally(() => setLoading(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, dealFilter, dealQ, dealPage]);
  // 打字时不要每个字母都打一次接口
  useEffect(() => {
    const t = setTimeout(loadDeals, dealQ ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadDeals, dealQ]);

  const loadProfiles = useCallback(() => {
    setLoading('bloggers');
    agentApi.signalsAll({ q: bloggerQ.trim() || undefined, page: bloggerPage })
      .then(setProfileData).catch(() => {})
      .finally(() => setLoading(''));
  }, [bloggerQ, bloggerPage]);
  useEffect(() => {
    if (view !== 'bloggers') return undefined;
    const t = setTimeout(loadProfiles, bloggerQ ? 300 : 0);
    return () => clearTimeout(t);
  }, [view, bloggerQ, bloggerPage, loadProfiles]);

  // 画像待审：平时不点开，出问题时才来翻
  const loadSigEvents = useCallback((status) => {
    agentApi.signalEvents({ status: status || 'pending' })
      .then(setSigEvents).catch(() => setSigEvents({ list: [], total: 0 }));
  }, []);
  useEffect(() => { if (view === 'bloggers') agentApi.signalEvents({ status: 'pending', limit: 5 }).then((d) => setSigEvents((p) => p || d)).catch(() => {}); }, [view]);

  const judgeSignal = async (id, action) => {
    // 「进画像」说明助理这次是对的，不用问
    const tag = action === 'approve' ? '' : await askWhy('这条画像哪儿不对？');
    if (action !== 'approve' && !tag) return;
    try { await agentApi.judgeSignal(id, action, tag); loadSigEvents(sigTab || 'pending'); }
    catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
  };

  const loadDrafts = useCallback(() => {
    // 'todo' = 已经能发、但还没人回看过的（后端按 approved/published + opsSeenAt 为空取）。
    // 以前拉的是 'ops_review'（等运营点通过），那个状态 2026-09-18 之后不再产生 ——
    // 继续拉它的话这一栏永远是空的，而真正该看的稿子谁也看不见。
    // 人工活动的稿子是 ops_review（要人点通过），单独一个队列
    Promise.all([
      agentApi.drafts('todo', draftQueue === 'todo' ? draftPage : 1),
      agentApi.drafts('ops_review', draftQueue === 'manual' ? draftPage : 1),
    ]).then(([todoR, manR]) => {
      setDraftTotal(todoR.total || 0);
      setManualDraftTotal(manR.total || 0);
      setDrafts((draftQueue === 'manual' ? manR.list : todoR.list) || []);
    }).catch(() => setDrafts([]));
  }, [draftPage, draftQueue]);

  const loadFeedback = useCallback(() => {
    agentApi.feedback(fbFilter, fbPage)
      .then((r) => { setFeedback(r.list || []); setFbTotal(r.total || 0); })
      .catch(() => setFeedback([]));
  }, [fbFilter, fbPage]);
  useEffect(() => { if (view === 'feedback') loadFeedback(); }, [view, loadFeedback]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const loadMatch = useCallback(() => {
    if (!shopId) return;
    setBusy('match');
    agentApi.match(shopId, 10)
      .then((d) => { setMatch(d); setPicked(new Set()); })
      .catch((e) => setMsg(e?.response?.data?.error || '匹配失败'))
      .finally(() => setBusy(''));
  }, [shopId]);
  useEffect(() => { if (shopId) loadMatch(); }, [shopId, loadMatch]);

  useEffect(() => { setDealPage(1); }, [dealFilter, dealQ, shopId]);
  useEffect(() => { setBloggerPage(1); }, [bloggerQ]);
  useEffect(() => { setFbPage(1); }, [fbFilter]);

  // 一张单子一次取全：商家条件、这单谈的、博主、体检。
  // 线上一个来回 1.5 秒，所以宁可一次多取一点，也不要点开一栏就再等一次
  const openDeal = (dealId, from) => {
    setDetailFrom(from || view);
    setBusy('detail');
    agentApi.dealDetail(dealId)
      .then(setDetail)
      .catch((e) => setMsg(e?.response?.data?.error || '这单打不开'))
      .finally(() => setBusy(''));
  };

  const openChat = (conversationId, from, label) => {
    if (!conversationId) return;
    setBackTo(from || view);
    setConvLabel(label || '');
    setConvId(conversationId);
    // 消息由 ConvPane 自己拉（它还要管 ws、轮询和发送态），这里只负责切过去
  };

  const openShop = (activityId) => {
    setShopId(activityId);
    setView('shops');
  };

  // 点一条待办：有对话就直接进对话（运营八成要在那儿回话），
  // 没有就落到它所在的店或稿件队列
  const openTodo = (t) => {
    if (t.kind === 'unmatched_shop') return;            // 这条点下面的绑定按钮
    // 新博主待审：审核在「博主管理」那个页面，直接送过去
    if (t.kind === 'blogger_pending') { window.location.hash = '#/admin/bloggers'; return; }
    // 「商家有异议」要判断的是这单本身对不对，直接进单子详情，别只给一条对话
    if (t.kind === 'objected') return openDeal(t.id, 'todo');
    if (t.convId) return openChat(t.convId, 'todo', t.who || '');
    if (t.kind === 'draft') return setView('drafts');
    if (t.kind === 'feedback') { setFbFilter('new'); return setView('feedback'); }
    if (t.activityId) return openShop(t.activityId);
  };

  // 灰度开关。商家端是线上产品，几十家真实商家都在用 ——
  // 没给某家店打开之前，他们连入口都看不到
  const toggleShop = async (a) => {
    const on = !a.tandianEnabled;
    if (on && !window.confirm(`给「${a.merchantName}」打开「博主探店」？打开后这家店的老板在商家端就能看到入口、能自己选档开始。`)) return;
    try {
      await agentApi.enableShop(a.merchantId, on);
      setMsg(on ? `已给「${a.merchantName}」打开` : `已关掉「${a.merchantName}」`);
      loadActivities();
    } catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
  };

  // 线下收了钱（或谈的时候答应送几位）之后，运营在这儿把名额补上。
  // 不走「买套餐」那条：套餐是固定档位，而线下谈出来的数目是任意的，
  // 硬塞进档位会让 planCode 跟实际名额对不上，对账时说不清。
  const addQuota = async (a) => {
    const raw = window.prompt(`给「${a.merchantName}」加几个博主名额？\n（填负数是减；减不到比已经用掉的还少）`, '1');
    if (raw === null) return;
    const count = Number(String(raw).trim());
    if (!Number.isInteger(count) || count === 0) { setMsg('要填非零整数'); return; }
    const note = window.prompt('备注一句（为什么加，比如「线下收款 $399」）—— 会记进这家店的名额流水', '') || '';
    try {
      const r = await agentApi.grantQuota(a.merchantId, count, note);
      setMsg(`「${r.name}」名额 ${r.quota} 个，已用 ${r.used}，还剩 ${r.remaining}`);
      loadActivities();
    } catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
  };

  // 退款。⚠ **点下去钱是真退的**（Stripe / 微信 / 支付宝都是真调），不可撤销。
  // 名额只收得回他还没用掉的那部分 —— 用掉的那几位是真有博主到过店，收不回来，
  // 所以退完要把「卖了几个 / 收回几个」都报出来，差额是运营要知道的账。
  const refund = async (o) => {
    if (!window.confirm(
      `给「${o.merchantName}」退 A$${o.monthlyFee}？\n\n`
      + `钱会真的退回去，不能撤销。\n`
      + `名额只收得回他还没用掉的部分（这一单卖了 ${o.bloggerQuota} 位）。`)) return;
    const reason = window.prompt('退款原因（会记进这条订单）', '') || '';
    if (!reason) return setMsg('退款要写原因');
    try {
      const r = await agentApi.refundOrder(o._id, reason);
      setMsg(r.alreadyDone
        ? '这一单之前就退过了'
        : `已退 A$${(r.refundedCents / 100).toFixed(2)}，收回 ${r.clawedBack}/${r.sold} 位`
          + (r.clawedBack < r.sold ? '（其余已经用掉，收不回来）' : ''));
      loadOrders(); if (shop?.merchantId) loadOrders(shop.merchantId);
    } catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
  };

  // 自动撮合（AGENT_AUTOPILOT）没打开时，那条跟进链整条是停的：
  // 超时换人、自动补位、催到店、催发布、复查帖子，一样都不会自己发生。
  // 演练时得有个地方能手动推一轮，否则这些永远测不到。
  const runSweep = async () => {
    setBusy('sweep');
    try {
      const r = await agentApi.sweep();
      const lines = r.log || [];
      setMsg(lines.length ? `跑完了：\n${lines.join('\n')}` : '跑完了，这一轮没有要处理的事');
      loadTodo(); loadDeals();
    } catch (e) { setMsg(e?.response?.data?.error || '没跑起来'); }
    finally { setBusy(''); }
  };

  const linkShop = async (t, merchantId, name) => {
    if (!window.confirm(`把「${t.who}」的登录号 ${t.mobile} 绑到「${name}」？绑完他那边马上能用。`)) return;
    try {
      await agentApi.linkShop(t.mobile, merchantId);
      setMsg(`已绑到「${name}」`);
      loadTodo();
    } catch (e) { setMsg(e?.response?.data?.error || '绑定失败'); }
  };

  // 处理完了：把 needsOps 摘掉，不然这条会一直挂在待办里
  const markHandled = async (t) => {
    if (!t.convId) return;
    const tag = await askWhy('助理这次哪儿不对？（它没做错就选「说不清」）');
    if (!tag) return;
    try { await agentApi.handled(t.convId, tag); loadTodo(); }
    catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
  };

  const invite = async () => {
    if (!picked.size) { setMsg('先勾几个博主'); return; }
    setBusy('invite');
    try {
      const r = await agentApi.invite(shopId, [...picked]);
      setMsg(`已发起 ${r.invited} 个邀请，agent 已开场`);
      loadMatch(); loadDeals();
    } catch (e) {
      setMsg(e?.response?.data?.error || '邀请失败');
    } finally { setBusy(''); }
  };

  const commit = async (dealId) => {
    if (!window.confirm('确认把这单写成真实报名记录？这会写进生产数据。')) return;
    try {
      await agentApi.commit(dealId);
      setMsg('已落库成报名记录');
      loadDeals();
    } catch (e) { setMsg(e?.response?.data?.error || '落库失败'); }
  };

  const shop = activities.find((a) => String(a.activityId) === shopId) || match?.activity;

  // 这两条得写在 loadOrders 和 shop 后面：依赖数组是渲染时求值的，
  // 写在声明前会 TDZ 报错，整页白屏
  useEffect(() => { if (view === 'orders') loadOrders(); }, [view, ordersPage, loadOrders]);
  useEffect(() => { if (shopId && shop?.merchantId) loadOrders(shop.merchantId); }, [shopId, shop?.merchantId, loadOrders]);

  // 线下收了钱，在这儿给他发名额。
  // ⚠ **点这一下就是发货** —— 走的是跟 Stripe/微信回调同一个出口（fulfillOrder），
  // 所以线上线下不会重复发，但也意味着没收到钱千万别点。
  const markPaid = async (o) => {
    if (!window.confirm(
      `确认已经收到「${o.merchantName}」的 A$${o.monthlyFee}？\n\n`
      + `点确定立刻给他开 ${o.bloggerQuota} 位博主名额。没收到钱别点。`)) return;
    const note = window.prompt('收款备注（怎么收的、收了多少）—— 会记进这条订单', o.note || '线下收款') || '线下收款';
    try {
      const r = await agentApi.markOrderPaid(o._id, note);
      setMsg(r.granted ? `已给「${o.merchantName}」开 ${r.bloggerQuota} 位` : '这一单之前就已经开过了，名额没重复发');
      loadOrders(); if (shop?.merchantId) loadOrders(shop.merchantId);
    } catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
  };
  const shownDeals = deals;   // 筛选在后端做了

  // ── 单子详情：从哪进来的都盖在上面，关掉回原处 ──
  if (detail && !convId) {
    return (
      <div style={st.page}>
        <AdminHeader title="这一单" back="/admin" />
        {msg && <div style={st.msg} onClick={() => setMsg('')}>{msg}</div>}
        <DealDetail
          d={detail}
          busy={busy === 'commit'}
          onBack={() => { setDetail(null); setView(detailFrom); }}
          onOpenMerchantConv={async () => {
            await agentApi.openMerchantConv(detail.deal._id);
            setDetail(await agentApi.dealDetail(detail.deal._id));   // 重取一次，会话 id 就挂上了
          }}
          onAddPost={async (platform, url) => {
            try { await agentApi.addPost(detail.deal._id, { platform, url }); setDetail(await agentApi.dealDetail(detail.deal._id)); }
            catch (e) { setMsg(e?.response?.data?.error || '贴不上'); }
          }}
          onDelPost={async (idx) => {
            try { await agentApi.delPost(detail.deal._id, idx); setDetail(await agentApi.dealDetail(detail.deal._id)); }
            catch (e) { setMsg(e?.response?.data?.error || '删不掉'); }
          }}
          onCommit={async () => {
            await commit(detail.deal._id);
            agentApi.dealDetail(detail.deal._id).then(setDetail).catch(() => {});
          }}
        />
      </div>
    );
  }

  // ── 对话面板：从哪进来的都盖在上面，关掉回原处 ──
  if (convId) {
    return (
      <div style={st.page}>
        <AdminHeader title={convLabel || '对话'} back="/admin" />
        <div style={st.body}>
          <div style={st.actionRow}>
            <GhostBtn small onClick={() => { setConvId(''); if (!detail) setView(backTo); }}>
              ← 返回{detail ? '这一单' : (NAV_LABEL[backTo] || '')}
            </GhostBtn>
          </div>
          {msg && <div style={{ ...st.msg, whiteSpace: 'pre-wrap' }} onClick={() => setMsg('')}>{msg}</div>}
          <ConvPane
            conversationId={convId}
            who={/商家/.test(convLabel) ? '商家' : '博主'}
            title={convLabel || '对话'}
            height={460}
            onHandled={(id) => askWhy('助理这次哪儿不对？（它没做错就选「说不清」）')
              .then((tag) => tag && agentApi.handled(id, tag).then(() => { setMsg('已标记处理完'); loadTodo(); }))}
            needsOps
          />
        </div>
      </div>
    );
  }

  return (
    <div style={st.page}>
      <AdminHeader title="撮合运营台" back="/admin" />

      {/* 演练模式横幅。不挂出来的话，「真博主一直没被联系过」这种事
          要等到有人问起才会发现 —— 而那时可能已经过去一周 */}
      {drill && (drill.testPool || drill.allowlist?.length > 0) && (
        <div style={st.drill}>
          <b>演练模式</b>
          {drill.testPool && <span style={st.drillItem}>· 只匹配测试博主，真博主不会被联系到</span>}
          {drill.allowlist?.length > 0 && (
            <span style={st.drillItem}>· 短信只发给 {drill.allowlist.join('、')}</span>
          )}
          <span style={st.drillItem}>· 自动邀请新博主：{drill.autoInvite ? '开' : '关（运营手动勾人）'}</span>
          <span style={st.drillTail}>测完记得让开发把这几个开关关掉，否则真博主永远收不到邀请</span>
        </div>
      )}

      <div style={st.bar}>
        <div style={st.tabs}>
          {[['todo', todo.total ? `待办 ${todo.total}` : '待办'],
          ['deals', '进行中'],
          // 人工审稿挡着博主发帖，数字要算进去
          ['drafts', (draftTotal + manualDraftTotal) ? `审稿 ${draftTotal + manualDraftTotal}` : '审稿'],
          ['bloggers', '博主'],
          ['shops', '店铺'],
          ['manual', '人工活动'],
          ['orders', '订单'],
          // 角标要的是「有几件没看」的总数，不是当前这一页有几条
          ['feedback', fbFilter === 'new' && fbTotal && view !== 'feedback'
            ? `反馈 ${fbTotal}` : '反馈']].map(([k, l]) => (
            <span
              key={k}
              style={{ ...st.tab, ...(view === k ? st.tabOn : {}) }}
              onClick={() => {
                setView(k);
                if (k !== 'shops') setShopId('');
                if (k === 'todo') loadTodo();
                if (k === 'drafts') loadDrafts();
                // 博主那一栏由它自己的 effect 拉（带搜索词），这里不用管
                if (k === 'feedback') loadFeedback();
                if (k === 'orders') loadOrders();
              }}
            >{l}</span>
          ))}
        </div>
      </div>

      {msg && <div style={st.msg} onClick={() => setMsg('')}>{msg}</div>}

      {/* 每次「改了助理做错的地方」都顺手问一句。六个按钮、点一下就走。
          不做下拉、不让填字 —— 多一步他就不会好好选，那这份数据就白收了。 */}
      {why && (
        <div style={st.whyMask} onClick={() => { why.resolve(''); setWhy(null); }}>
          <div style={st.whyBox} onClick={(e) => e.stopPropagation()}>
            <div style={st.whyTitle}>{why.title}</div>
            <div style={st.whyList}>
              {WHY_TAGS.map(([tag, label]) => (
                <div
                  key={tag}
                  style={st.whyItem}
                  onClick={() => { why.resolve(tag); setWhy(null); }}
                >{label}</div>
              ))}
            </div>
            <div style={st.whySkip} onClick={() => { why.resolve(''); setWhy(null); }}>取消</div>
          </div>
        </div>
      )}

      <div style={st.body}>
        {/* ── 待办：所有店加起来要人管的事 ── */}
        {view === 'todo' && (
          <>
            <div style={st.actionRow}>
              <GhostBtn small onClick={runSweep}>{busy === 'sweep' ? '跑着…' : '跑一轮跟进'}</GhostBtn>
              <span style={st.sub}>
                跟进本来每 5 分钟自己跑一轮（超时换人、催到店、催发布、复查帖子）。
                不想等就点一下，立刻推一轮并把这一轮干了什么列出来
              </span>
            </div>
            {todo.items.length === 0
            ? <EmptyState text="没有要你处理的事。所有店都看过了。" />
            : (
              <>
                <div style={st.warn}>所有店、所有活动加起来要人管的事都在这儿。点一条直接进去处理。</div>
                {todo.items.map((t) => (
                  <div key={t.kind + t.id} style={st.card} onClick={() => openTodo(t)}>
                    <div style={st.cardTop}>
                      <span style={{ ...st.todoTag, background: TODO_TONE[t.kind] || '#6b7280' }}>
                        {TODO_LABEL[t.kind] || '待处理'}
                      </span>
                      <span style={st.name}>{t.who}</span>
                      <span style={st.sub}>{t.merchantName}{t.activityName ? ` · ${t.activityName}` : ''}</span>
                    </div>
                    <div style={st.reason}>{t.reason}</div>
                    {/* 最后一句。只有「agent 叫人来看一眼」的话，不点进去不知道出了什么事 */}
                    {t.last && <div style={st.todoLast}>最后一句：{t.last}</div>}
                    {(t.images || []).length > 0 && (
                      <div style={st.shots}>
                        {t.images.slice(0, 4).map((u) => <img key={u} src={u} alt="" style={st.shot} />)}
                      </div>
                    )}
                    {t.kind === 'unmatched_shop' && (t.candidateIds || []).length > 0 && (
                      <div style={st.actions}>
                        {t.candidateIds.map((id, i) => (
                          <GhostBtn key={id} small onClick={(e) => { e.stopPropagation(); linkShop(t, id, t.candidateNames[i]); }}>
                            绑到「{t.candidateNames[i]}」
                          </GhostBtn>
                        ))}
                      </div>
                    )}
                    {/* 「处理完了」放左边、时间放右边 —— 右下角被「报个问题」那个
                        悬浮按钮占着（position:fixed，right:20 bottom:24），
                        按钮在右边的话滚到某个位置就会被它盖住点不着。
                        运营 2026-09-17 反馈过。时间被盖住无所谓，按钮不行。 */}
                    <div style={st.todoBottom}>
                      {t.convId ? (
                        <span style={st.handled} onClick={(e) => { e.stopPropagation(); markHandled(t); }}>处理完了</span>
                      ) : <span />}
                      <span style={st.todoAt}>{t.at ? timeAgo(t.at) : ''}</span>
                    </div>
                  </div>
                ))}
                <Pager page={todo.page || 1} total={todo.total || 0} pageSize={todo.pageSize || 20} onPage={setTodoPage} />
              </>
            )}
          </>
        )}


        {/* ── 订单：哪家店买了什么 ──
            运营 2026-09-21 反馈「运营侧缺少入口查看商家付费相关申请」，要的就是这个。
            **这里不挡任何人**：商家点购买时名额当场就发了，这一页是账本不是审批台。
            「已收款」是运营自己勾的对账标记，跟名额无关 —— 现在点购买那一下不产生
            任何支付（商家端那个按钮没接券宝宝收银台），所以「买了」和「付了」必须分开看。 */}
        {view === 'orders' && (
          <>
            <div style={st.warn}>
              商家在套餐页点购买会记一条「待付款」，**付款成功才发名额**（微信 / 支付宝 / 信用卡的回调自动发）。
              一直停在「待付款」的是他中途放弃了，不用管；线下收了钱的点「已收款，开名额」。
            </div>
            <div style={st.termsStrip}>
              <span style={st.termsK}>合计</span>
              下单 {orders.summary?.orders || 0} 笔 A${orders.summary?.fee || 0}
              {' · '}已到账 {orders.summary?.paidOrders || 0} 笔 <b>A${orders.summary?.paid || 0}</b>
              {(orders.summary?.orders || 0) > (orders.summary?.paidOrders || 0) && (
                <span style={{ color: '#9ca3af' }}>
                  {' · 待付款 '}{orders.summary.orders - orders.summary.paidOrders} 笔（多半是点了没付，不用管）
                </span>
              )}
            </div>
            {orders.rows.length === 0
              ? <EmptyState text="还没有人买过套餐。" />
              : orders.rows.map((o) => (
                <div key={o._id} style={st.card}>
                  <div style={st.cardTop}>
                    <span style={{ ...st.todoTag, background: (ORDER_STATE[o.status] || ORDER_STATE.pending)[1] }}>
                      {(ORDER_STATE[o.status] || ORDER_STATE.pending)[0]}
                    </span>
                    <span style={st.name}>{o.merchantName || o.merchantId}</span>
                    <span style={st.sub}>{o.planName} · {o.bloggerQuota} 位 · {PAY_LABEL[o.provider] || o.provider}</span>
                  </div>
                  <div style={st.reason}>
                    A${o.monthlyFee}/月{o.note ? ` · ${o.note}` : ''}
                    {o.providerRef ? ` · 流水号 ${o.providerRef}` : ''}
                    {o.status === 'refunded' && (
                      <span style={{ color: '#7c3aed' }}>
                        {` · 退了 A$${(o.refundedCents / 100).toFixed(2)}，收回 ${o.quotaClawedBack}/${o.bloggerQuota} 位`}
                        {o.refundReason ? `（${o.refundReason}）` : ''}
                      </span>
                    )}
                  </div>
                  <div style={st.todoBottom}>
                    {/* 已到账的不给再点 —— 幂等锁在后端拦着，但按钮就不该在那儿引人手滑 */}
                    {o.status === 'pending' && (
                      <span style={st.handled} onClick={() => markPaid(o)}>已收款，开 {o.bloggerQuota} 位</span>
                    )}
                    {o.status === 'paid' && (
                      <span style={{ ...st.handled, color: '#7c3aed' }} onClick={() => refund(o)}>退款</span>
                    )}
                    {!['pending', 'paid'].includes(o.status) && <span />}
                    <span style={st.todoAt}>{o.createdAt ? timeAgo(o.createdAt) : ''}</span>
                  </div>
                </div>
              ))}
            <Pager page={ordersPage} total={orders.total || 0} pageSize={20} onPage={setOrdersPage} />
          </>
        )}

        {/* ── 进行中：一张总表，需要时再筛 ── */}
        {view === 'deals' && !shopId && (
          <>
            <div style={st.chipRow}>
              {[['live', '在谈 + 已谈成'], ['agreed', '只看已谈成'], ['all', '全部']].map(([k, l]) => (
                <span key={k} style={{ ...st.filterChip, ...(dealFilter === k ? st.filterChipOn : {}) }}
                  onClick={() => setDealFilter(k)}>{l}</span>
              ))}
              <span style={st.sub}>共 {dealTotal} 单</span>
            </div>
            <input style={st.search} value={dealQ} placeholder="搜博主名字、电话或店名"
              onChange={(e) => setDealQ(e.target.value)} />
            {dealFilter === 'live' && talkingShops.map((c) => (
              <div key={c.conversationId} style={st.card}>
                <div style={st.cardTop}>
                  <span style={st.name}>{c.merchantName}</span>
                  <span style={st.status}>商家在谈，还没发邀请</span>
                  {c.needsOps && <span style={st.chipNo}>要运营看</span>}
                  <span style={st.sub}>{timeAgo(c.lastMessageAt)}</span>
                </div>
                <div style={st.terms}>{c.opsReason || c.lastMessagePreview}</div>
                <div style={st.actions}>
                  <GhostBtn small onClick={() => openChat(c.conversationId, 'deals', `${c.merchantName} · 商家`)}>商家那边</GhostBtn>
                </div>
              </div>
            ))}
            {loading === 'deals' && shownDeals.length === 0
              ? <EmptyState text="加载中…" />
              : shownDeals.length === 0
                ? (talkingShops.length && dealFilter === 'live' ? null : <EmptyState text="没有符合的撮合单" />)
                : shownDeals.map((d) => (
                  <DealRow key={d._id} d={d}
                    onOpen={() => openDeal(d._id, 'deals')}
                    onChat={() => openChat(d.conversationId, 'deals', `${d.bloggerName || d.bloggerPhone} · 博主`)}
                    onMerchantChat={() => openChat(d.merchantConversationId, 'deals', `${d.merchantName} · 商家`)}
                    onCommit={() => commit(d._id)} />
                ))}
            <Pager page={dealPage} total={dealTotal} onPage={setDealPage} />
          </>
        )}

        {/* ── 审稿：跨店队列 ── */}
        {view === 'drafts' && (
          <div style={st.chipRow}>
            {[['manual', `人工审稿${manualDraftTotal ? ` ${manualDraftTotal}` : ''}`], ['todo', `事后回看${draftTotal ? ` ${draftTotal}` : ''}`]].map(([k, l]) => (
              <span key={k} style={{ ...st.filterChip, ...(draftQueue === k ? st.filterChipOn : {}) }}
                onClick={() => { setDraftQueue(k); setDraftPage(1); }}>{l}</span>
            ))}
          </div>
        )}
        {view === 'drafts' && draftQueue === 'manual' && (
          drafts.length === 0
            ? <EmptyState text="没有等人工审的稿子。人工活动的稿子过了机器检查会到这里。" />
            : (
              <>
                <div style={st.warn}>
                  人工活动的稿子：机器已经查过硬指标，<b>要你点「通过」博主才能发</b>。
                  要商家看的，点「复制审稿链接给商家」发给他：商家打开就能看文字、图片和视频，可以直接通过或退回（退回意见会原样发给博主）。不用下载图片。
                </div>
                {drafts.map((d) => <DraftCard askWhy={askWhy} key={d._id} d={d} onDone={loadDrafts} setMsg={setMsg} />)}
                <Pager page={draftPage} total={manualDraftTotal} onPage={setDraftPage} />
              </>
            )
        )}
        {view === 'drafts' && draftQueue === 'todo' && (
          drafts.length === 0
            ? <EmptyState text="没有要回看的稿子。机器核对没过的已经自动退回博主改了，不会到这里。" />
            : (
              <>
                <div style={st.warn}>
                  这些稿子<b>机器已经放行、博主现在就能发</b>，不用等你点通过 ——
                  所以这一栏不挡任何人，是给你事后过一眼的。
                  硬指标（字数、图片、必带话题、店名、必拍菜、广告披露）机器都查过了；
                  你只看机器判不了的：文案是不是太 AI、图拍得有没有食欲、店铺露出够不够。
                  看完给个分，分会进博主画像影响以后推不推他；有建议就留一句，会发给他。
                </div>
                {drafts.map((d) => <DraftCard askWhy={askWhy} key={d._id} d={d} onDone={loadDrafts} setMsg={setMsg} />)}
                <Pager page={draftPage} total={draftTotal} onPage={setDraftPage} />
              </>
            )
        )}

        {/* ── 人工活动：不用商家 App 的店，运营建活动、人工挑人和审稿 ── */}
        {view === 'manual' && <ManualActivities setMsg={setMsg} openChat={openChat} openDeal={openDeal} />}

        {/* ── 博主：全部画像，跟店无关 ── */}
        {view === 'bloggers' && (
          <>
            {/* 画像待审。平时收着不占地方，有待审的才显眼 ——
                助理学到的自由文本不会直接进画像，得有人看一眼 */}
            <div style={st.sigBar}>
              <span
                style={sigEvents?.pendingCount > 0 ? st.sigBadgeOn : st.sigBadge}
                onClick={() => { const t = sigTab === 'pending' ? '' : 'pending'; setSigTab(t); if (t) loadSigEvents('pending'); }}
              >
                画像待审 {sigEvents?.pendingCount || 0}
              </span>
              <span style={st.sigLink}
                onClick={() => { const t = sigTab === 'active' ? '' : 'active'; setSigTab(t); if (t) loadSigEvents('active'); }}>
                最近生效的
              </span>
              <span style={st.sub}>助理学到的东西从这儿过。结构化的直接生效但能撤，自由文本要你点头才进画像</span>
            </div>

            {sigTab && (
              <div style={st.card}>
                {!sigEvents?.list?.length
                  ? <div style={st.reason}>{sigTab === 'pending' ? '没有待审的。' : '最近没有生效的变更。'}</div>
                  : sigEvents.list.map((e) => (
                    <div key={e._id} style={st.sigRow}>
                      <div style={st.cardTop}>
                        <span style={e.layer === 3 ? st.badgeWarn : st.badgeOk}>{e.layer === 3 ? '自由文本' : '结构化'}</span>
                        <span style={st.name}>{e.bloggerName || e.phone}</span>
                        <span style={st.sub}>{SIG_FIELD[e.field] || e.field}</span>
                        <span style={st.sub}>{timeAgo(e.createdAt)}</span>
                      </div>
                      <div style={st.reason}>{e.value}</div>
                      {e.reason && <div style={st.gaps}>{e.reason}</div>}
                      <div style={st.actions}>
                        {e.status === 'pending' && (
                          <>
                            <PrimaryBtn small onClick={() => judgeSignal(e._id, 'approve')}>进画像</PrimaryBtn>
                            <GhostBtn small onClick={() => {
                              const v = window.prompt('改成什么再进画像？', e.value);
                              if (!v || !v.trim() || v.trim() === e.value) return;
                              agentApi.judgeSignal(e._id, 'approve', '', v.trim())
                                .then(() => { setMsg('改好了，已进画像'); loadSigEvents(sigTab || 'pending'); })
                                .catch((err) => setMsg(err?.response?.data?.error || '没成功'));
                            }}>改了再进</GhostBtn>
                            <GhostBtn small onClick={() => judgeSignal(e._id, 'reject')}>不要</GhostBtn>
                          </>
                        )}
                        {e.status === 'active' && (
                          <GhostBtn small onClick={() => judgeSignal(e._id, 'revert')}>从画像里撤掉</GhostBtn>
                        )}
                        {e.conversationId && (
                          <GhostBtn small onClick={() => openChat(e.conversationId, 'bloggers', e.bloggerName || e.phone)}>
                            看当时的对话
                          </GhostBtn>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            <input style={st.search} value={bloggerQ} placeholder="搜名字、电话或小红书号"
              onChange={(e) => setBloggerQ(e.target.value)} />
          </>
        )}
        {view === 'bloggers' && (
          !profileData ? <EmptyState text="加载中…" /> : (
            <>
              <div style={st.sumCard}>
                <span style={st.sumNum}>{profileData.avgCompleteness}%</span>
                <span style={st.sumLabel}>
                  全库画像平均完整度。每聊一次补一点，匹配就准一点。
                  {bloggerQ ? `搜到 ${profileData.total} 个人` : `共 ${profileData.total} 个博主`}，
                  按最近更新排，一页 {profileData.pageSize || 20} 个。
                </span>
              </div>
              {loading === 'bloggers' && <div style={st.sub}>加载中…</div>}
              {profileData.list.map((p) => <BloggerCard key={p.phone} p={p} setMsg={setMsg} openChat={openChat} onChanged={loadProfiles} />)}
              {profileData.list.length === 0 && <EmptyState text="没搜到这个人" />}
              <Pager page={profileData.page || 1} total={profileData.total || 0}
                pageSize={profileData.pageSize || 20} onPage={setBloggerPage} />
            </>
          )
        )}

        {/* ── 反馈：运营报上来的问题 ── */}
        {view === 'feedback' && (
          <>
            <div style={st.chipRow}>
              {[['new', '待看'], ['triaged', '看过了'], ['fixed', '已修'], ['wontfix', '不改'], ['all', '全部']].map(([k, l]) => (
                <span key={k} style={{ ...st.filterChip, ...(fbFilter === k ? st.filterChipOn : {}) }}
                  onClick={() => setFbFilter(k)}>{l}</span>
              ))}
            </div>
            {feedback.length === 0
              ? <EmptyState text="没有反馈。右下角那个「报个问题」谁都能点。" />
              : feedback.map((f) => (
                <FeedbackCard key={f._id} f={f} onDone={() => { loadFeedback(); loadTodo(); }}
                  onOpen={() => { if (f.refs?.conversationId) openChat(f.refs.conversationId, 'feedback', ''); }}
                  setMsg={setMsg} />
              ))}
            <Pager page={fbPage} total={fbTotal} onPage={setFbPage} />
          </>
        )}

        {/* ── 店铺列表：唯一按店走的入口 ── */}
        {view === 'shops' && !shopId && (
          activities.length === 0 ? <EmptyState text={loading === 'shops' ? '加载中…' : '还没有开通探店的店'} /> : activities.map((a) => (
            <div key={a.merchantId} style={st.card}
              onClick={() => { if (a.activityId) openShop(String(a.activityId)); }}>
              <div style={st.cardTop}>
                <span style={{ ...st.todoTag, background: a.tandianEnabled ? '#16a34a' : '#9ca3af' }}>
                  {a.tandianEnabled ? '已开' : '未开'}
                </span>
                <span style={st.name}>{a.merchantName}</span>
                {/* 没有容器 = 商家还没跟助理把条件谈齐，详情页（单子、匹配、邀请）
                    整块都是按 activityId 走的，所以这种卡片点不进去，直说原因 */}
                <span style={st.sub}>{a.activityId ? a.activityName : '还没开始谈'}</span>
                {a.activityId && todo.byActivity?.[String(a.activityId)] > 0 && (
                  <span style={{ ...st.todoTag, background: '#FA1C51' }}>{todo.byActivity[String(a.activityId)]} 件待办</span>
                )}
              </div>
              {/* 进度（2026-10-01 会议）：在找几位、约好几位、做完几单，一条进度条看整体 */}
              {a.neededCount > 0 && (
                <div style={st.progWrap}>
                  <div style={st.progText}>
                    {a.active && <span style={st.progLive}>进行中</span>}
                    在找 {a.neededCount} 位 · 在谈 {a.talkingCount} · 约好 {a.bookedCount} · 完成 {a.doneCount}
                  </div>
                  <div style={st.progBar}>
                    <div style={{ ...st.progDone, width: `${Math.min(100, (a.doneCount / a.neededCount) * 100)}%` }} />
                    <div style={{ ...st.progBooked, width: `${Math.min(100 - Math.min(100, (a.doneCount / a.neededCount) * 100), (a.bookedCount / a.neededCount) * 100)}%` }} />
                  </div>
                </div>
              )}
              <div style={st.terms}>
                谈成 {a.agreedCount} · 进行中 {a.talkingCount} · 累计 {a.dealCount} 单
                {/* 这家给过钱没有 —— 运营翻列表时最想先看到的一条。
                    只算到账的，停在待付款的不算，否则一眼看过去全是虚的 */}
                {a.paidOrders > 0
                  ? <span style={{ color: '#16a34a' }}> · 已付 {a.paidOrders} 笔 A${a.paidTotal}
                    {a.lastPaidAt ? `（最近 ${timeAgo(a.lastPaidAt)}）` : ''}</span>
                  : <span style={{ color: '#9ca3af' }}> · 没付过钱</span>}
              </div>
              <div style={st.actions}>
                <GhostBtn small onClick={(e) => { e.stopPropagation(); toggleShop(a); }}>
                  {a.tandianEnabled ? '关掉商家端入口' : '给这家店打开'}
                </GhostBtn>
                <GhostBtn small onClick={(e) => { e.stopPropagation(); addQuota(a); }}>加名额</GhostBtn>
              </div>
            </div>
          ))
        )}
        {view === 'shops' && !shopId && <Pager page={shopPage} total={actTotal} onPage={setShopPage} />}

        {/* ── 某家店：挑人发邀请 + 这家店的单子 ── */}
        {view === 'shops' && shopId && (
          <>
            <div style={st.actionRow}>
              <GhostBtn small onClick={() => setShopId('')}>← 所有店铺</GhostBtn>
              <span style={st.shopName}>{shop?.merchantName} · {shop?.name}</span>
              {/* 商家跟助理只有一条会话，不挂在任何一张单子上，所以入口放在店这一层 */}
              {shop?.merchantConversationId && (
                <GhostBtn small onClick={() => openChat(shop.merchantConversationId, 'shops', `${shop.merchantName} · 商家`)}>
                  跟商家的对话
                </GhostBtn>
              )}
            </div>
            {/* 这家店买过什么。运营反馈「店铺那里也查得到」 */}
            {shopOrders.rows.length > 0 && (
              <div style={st.termsStrip}>
                <span style={st.termsK}>买过</span>
                <span style={{ ...st.handled, marginRight: 8 }} onClick={() => setView('orders')}>看全部订单 →</span>
                {shopOrders.summary?.orders || 0} 笔 · 下单 A${shopOrders.summary?.fee || 0} · 已收 A${shopOrders.summary?.paid || 0}
                {shopOrders.rows.slice(0, 3).map((o) => (
                  <span key={o._id} style={st.sub}>
                    {' ｜ '}{o.planName} A${o.monthlyFee} {o.status === 'paid' ? '已到账' : '待付款'} {timeAgo(o.createdAt)}
                  </span>
                ))}
              </div>
            )}
            {match?.terms == null ? (
              <div style={st.warn}>
                这个商家还没配探店条件（套餐价值、可接待时段等），agent 谈判会缺依据。
                商家可以在商家端页面填，或你在这里替他填。
              </div>
            ) : (
              /* 挑人之前先看一眼这家店要什么，不用为了看条件再点进某一单 */
              <div style={st.termsStrip}>
                <span style={st.termsK}>这家店要</span>
                套餐 ${match.terms.mealValue || 0}
                {match.terms.maxMealValue ? `（可让到 $${match.terms.maxMealValue}）` : ''}
                {match.terms.minFollowers ? ` · ${match.terms.minFollowers} 粉以上` : ''}
                {match.terms.neededCount ? ` · 这轮招 ${match.terms.neededCount} 位` : ''}
                {match.terms.peopleAllowed ? ` · 最多 ${match.terms.peopleAllowed} 人到店` : ''}
                {(match.terms.availability || []).length ? ` · 可接待 ${fmtSlots(match.terms.availability)}` : ''}
                {match.terms.allowPaid ? ` · 可谈现金到 $${match.terms.maxPaidFee || 0}` : ' · 纯置换'}
                {(match.terms.mustTags || []).length ? ` · 必带 ${match.terms.mustTags.join('、')}` : ''}
              </div>
            )}
            <div style={st.actionRow}>
              <GhostBtn small onClick={loadMatch}>{busy === 'match' ? '算分中…' : '重新匹配'}</GhostBtn>
              <PrimaryBtn small onClick={invite}>
                {busy === 'invite' ? '发起中…' : `发起邀请（已选 ${picked.size}）`}
              </PrimaryBtn>
            </div>
            {(match?.candidates || []).length === 0 ? (
              <EmptyState text="没有匹配到候选博主" />
            ) : (
              match.candidates.map((c) => (
                <div key={c.phone} style={st.card}>
                  <div style={st.cardTop}>
                    <input
                      type="checkbox"
                      checked={picked.has(c.phone)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(c.phone); else next.delete(c.phone);
                        setPicked(next);
                      }}
                    />
                    <span style={st.score}>{c.score}</span>
                    <span style={st.name}>{c.name || c.phone}</span>
                    <span style={st.sub}>{c.xhsAccount} · {c.xhsFollowers} 粉</span>
                  </div>
                  {c.bio && <div style={st.bio}>{c.bio}</div>}
                  {c.reasons.map((r, i) => <div key={i} style={st.reason}>· {r}</div>)}
                  {c.gaps.length > 0 && <div style={st.gaps}>agent 会问：{c.gaps.join('、')}</div>}
                </div>
              ))
            )}

            <div style={st.sectionTitle}>这家店的单子（{dealTotal}）</div>
            {deals.length === 0
              ? <EmptyState text={loading === 'deals' ? '加载中…' : '还没有撮合单，上面挑几个博主发邀请'} />
              : deals.map((d) => (
                <DealRow key={d._id} d={d}
                  onOpen={() => openDeal(d._id, 'shops')}
                  onChat={() => openChat(d.conversationId, 'shops', `${d.bloggerName || d.bloggerPhone} · 博主`)}
                  onMerchantChat={() => openChat(d.merchantConversationId, 'shops', `${d.merchantName} · 商家`)}
                  onCommit={() => commit(d._id)} />
              ))}
            <Pager page={dealPage} total={dealTotal} onPage={setDealPage} />
          </>
        )}
      </div>
      <div style={{ height: 20 }} />
      <ReportIssue context={{ view, refs: { activityId: shopId, conversationId: convId } }} />
    </div>
  );
}

// 一条运营报上来的问题。看完要留一句结论 ——
// 下次同一个问题再报上来，能直接看到上次怎么判的
// 反馈的编号：后端按提交先后给的顺序号，从 1 开始。
// 用数字不用 ObjectId 后六位 —— 十六进制没法念，运营跟开发对不上号
const fbNo = (f) => '#' + (f.seq || String(f._id).slice(-4));

function FeedbackCard({ f, onDone, onOpen, setMsg }) {
  const [verdict, setVerdict] = useState(f.verdict || '');
  const [busy, setBusy] = useState(false);
  const [big, setBig] = useState('');

  const judge = async (status) => {
    setBusy(true);
    try { await agentApi.judgeFeedback(f._id, { status, verdict }); onDone(); }
    catch (e) { setMsg(e?.response?.data?.error || '没保存上'); }
    finally { setBusy(false); }
  };

  return (
    <div style={st.card}>
      <div style={st.cardTop}>
        <span style={{ ...st.todoTag, background: f.status === 'new' ? '#0f766e' : '#9ca3af' }}>
          {FB_STATUS[f.status] || f.status}
        </span>
        {/* 短编号：列表上看不到编号的话，运营跟开发对不上号 ——
            「那条博主不发帖的」要靠描述互相猜。点一下能复制 */}
        <span
          style={st.fbId}
          title="点一下复制编号"
          onClick={() => { try { navigator.clipboard.writeText(fbNo(f)); setMsg(`已复制 ${fbNo(f)}`); } catch { /* 没剪贴板就算了 */ } }}
        >{fbNo(f)}</span>
        <span style={st.name}>{f.reporter || '运营'}</span>
        <span style={st.sub}>{timeAgo(f.createdAt)}</span>
      </div>
      <div style={{ ...st.reason, whiteSpace: 'pre-wrap', color: '#374151' }}>{f.text || '（只贴了图）'}</div>

      {(f.images || []).length > 0 && (
        <div style={st.shots}>
          {f.images.map((u) => <img key={u} src={u} alt="" style={st.shot} onClick={() => setBig(u)} />)}
        </div>
      )}

      {/* 现场信息。没有这些，一句「刚才那个博主的时间不对」事后查不了 */}
      <div style={st.gaps}>
        当时在：{f.view ? NAV_LABEL[f.view] || f.view : '（未知）'}
        {f.page ? ` · ${f.page}` : ''}
        {f.refs?.activityId ? ' · 有店铺上下文' : ''}
        {f.refs?.conversationId ? ' · 有对话上下文' : ''}
      </div>

      {/* 已经有结论就当正文显示 —— 结论是给运营读的，摆在输入框里像是在等人改 */}
      {f.verdict ? (
        <div style={st.verdictText}>{f.verdict}</div>
      ) : (
        <input
          style={st.verdict}
          value={verdict}
          placeholder="查完写一句：是 bug 还是预期行为，改不改"
          onChange={(e) => setVerdict(e.target.value)}
        />
      )}
      <div style={st.actions}>
        {f.refs?.conversationId && <GhostBtn small onClick={onOpen}>去看那条对话</GhostBtn>}
        <GhostBtn small disabled={busy} onClick={() => judge('triaged')}>看过了</GhostBtn>
        <PrimaryBtn small disabled={busy} onClick={() => judge('fixed')}>已修</PrimaryBtn>
        <GhostBtn small disabled={busy} onClick={() => judge('wontfix')}>不改</GhostBtn>
      </div>

      {big && (
        <div style={st.lightbox} onClick={() => setBig('')}>
          <img src={big} alt="" style={st.lightboxImg} />
        </div>
      )}
    </div>
  );
}

const WD = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const fmtSlots = (a) => (a || []).map((s) => `${WD[s.weekday]} ${s.from}-${s.to}`).join('、');
const fmtList = (a) => ((a || []).length ? a.join('、') : '');
const fmtTime = (v) => (v ? new Date(v).toLocaleString('zh-CN') : '');

// 一行「标签 + 值」。值是空的就整行不显示 —— 后台最怕的是满屏「—」，
// 有内容的那几行反而被淹掉
function Row({ k, v }) {
  if (v === '' || v === null || v === undefined || v === 0) return null;
  return (
    <div style={st.dRow}>
      <span style={st.dK}>{k}</span>
      <span style={st.dV}>{v}</span>
    </div>
  );
}

function Panel({ title, sub, children }) {
  return (
    <div style={st.panel}>
      <div style={st.panelTop}>
        <span style={st.panelTitle}>{title}</span>
        {sub && <span style={st.sub}>{sub}</span>}
      </div>
      {children}
    </div>
  );
}

// 一张单子的全貌。运营判断「这单有没有问题」原来要翻三个地方再自己脑内比对，
// 这里三栏并排放，并且把该比的都比好了摆在最上面。

// ── 一边的对话（博主那边 / 商家那边）───────────────────────────
//
// 单独拎成组件，是为了让「这一单」页面能左右各挂一个、两边对着看。
// 以前只能一次看一边，运营要核对「博主说的时间跟商家给的档位对不对得上」
// 就得来回切页面，切回来还得重新找位置。
//
// 这里解决运营实际踩的两个坑：
//   1. 「发给谁」以前只有输入框上一行灰字。运营把内部批注当成回复，连打四条，
//      博主那边一个字没收到 —— 现在是一排选项 + 发送按钮上直接写着发给谁。
//   2. 按钮一排全长一样，分不清哪个是干嘛的 —— 现在按用途分组，
//      「让 agent 接一句」是主按钮，人工接管那两个只出当前能点的那一个。
function ConvPane({ conversationId, who, title, sub, needsOps, height = 420, onNote, onHandled, onCreate }) {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState('');
  const [takeover, setTakeover] = useState(null);   // null = 还不知道
  const [tip, setTip] = useState('');

  const reload = useCallback(async () => {
    if (!conversationId) return;
    try { setMessages(await agentApi.messages(conversationId)); } catch { /* 网络抖一下不清空已有的 */ }
  }, [conversationId]);

  useEffect(() => { reload(); }, [reload]);

  // ChatBox 自己连了 WebSocket，新消息会实时推进来；这里只兜底一层轮询，
  // 免得 ws 断了运营盯着一个不动的页面以为对方没回（线上 /im 那条 location 掉过一次）
  useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  // 商家那一边的会话是懒建的（商家第一次说话、或助理第一次通知他才建），
  // 所以这里经常是空的。光写一句「还没有对话」等于把运营堵死在这儿 ——
  // 给一个按钮让他当场把会话开出来，开完直接就能发。
  if (!conversationId) {
    return (
      <div style={st.paneWrap}>
        <div style={st.paneHead}><span style={st.paneTitle}>{title}</span></div>
        {sub && <div style={st.paneSub}>{sub}</div>}
        <div style={st.reason}>
          还没有这一边的对话 —— {who}还没在 App 里说过话，助理也还没通知过他。
        </div>
        {onCreate && (
          <div style={{ ...st.paneActs, marginTop: 8 }}>
            <PrimaryBtn small disabled={busy === 'new'}
              onClick={() => { setBusy('new'); Promise.resolve(onCreate())
                .catch((e) => setTip(e?.response?.data?.error || '开不了'))
                .finally(() => setBusy('')); }}>
              {busy === 'new' ? '开着…' : `给${who}开一条对话`}
            </PrimaryBtn>
          </div>
        )}
        {tip && <div style={st.paneTip} onClick={() => setTip('')}>{tip}</div>}
      </div>
    );
  }

  const act = async (key, fn, okTip) => {
    setBusy(key);
    try { await fn(); if (okTip) setTip(okTip); await reload(); }
    catch (e) { setTip(e?.response?.data?.error || '没成功'); }
    finally { setBusy(''); }
  };

  const doSend = async (text, mode) => {
    if (mode === 'note') {
      await act('send', () => agentApi.opsSay(conversationId, text), '已记为内部批注 —— 对方看不到，agent 下次会读到');
      onNote && onNote();
      return;
    }
    await act('send', async () => {
      const r = await agentApi.opsSend(conversationId, text);
      // 推送失败要说出来。存是存下了，但对方不一定知道有新消息
      setTip(r?.delivered === 'sent' ? `已发给${who}，并且推送过去了`
        : r?.delivered === 'dry' ? `已发给${who}（短信通道关着，只进了站内）`
        : `已发给${who}，但推送没成功（${r?.delivered || '未知'}）—— 他不一定看得到，必要时另外联系`);
    });
  };

  return (
    <div style={st.paneWrap}>
      <div style={st.paneHead}>
        <span style={st.paneTitle}>{title}</span>
        {needsOps && <span style={st.paneDot}>要你看</span>}
        <span style={st.paneState}>
          {takeover === null ? '' : (takeover ? '已转人工 · agent 不自动回' : 'agent 自动回')}
        </span>
      </div>
      {sub && <div style={st.paneSub}>{sub}</div>}

      <div style={st.paneActs}>
        <PrimaryBtn small disabled={busy === 'kick'}
          onClick={() => act('kick', () => agentApi.kick(conversationId), '已让 agent 接一句')}>
          {busy === 'kick' ? '思考中…' : '让 agent 接一句'}
        </PrimaryBtn>
        {takeover ? (
          <GhostBtn small onClick={() => act('to', () => agentApi.takeover(conversationId, false).then(() => setTakeover(false)), '已交回 agent，它会继续自动回')}>
            交回 agent
          </GhostBtn>
        ) : (
          <GhostBtn small onClick={() => act('to', () => agentApi.takeover(conversationId, true).then(() => setTakeover(true)), '已转人工，agent 不再自动回这一边')}>
            转人工（agent 闭嘴）
          </GhostBtn>
        )}
        {needsOps && onHandled && (
          <GhostBtn small onClick={() => onHandled(conversationId)}>这条处理完了</GhostBtn>
        )}
      </div>

      {tip && <div style={st.paneTip} onClick={() => setTip('')}>{tip}</div>}

      <ChatBox
        conversationId={conversationId}
        messages={messages}
        sending={busy === 'send'}
        onSend={doSend}
        imAuth={{ role: 'ops', token: localStorage.getItem('admin_token') || '' }}
        showMeta
        onIncoming={(m) => setMessages((prev) => mergeIncoming(prev, m))}
        placeholder={`打字…（默认发给${who}）`}
        height={height}
        sendModes={[
          { key: 'send', label: `发给${who}`, hint: `${who}立刻收到，并且推送过去` },
          { key: 'note', label: '只记给 agent 看', hint: '内部批注，对方看不到，agent 下次回话时会读到', danger: true },
        ]}
      />
    </div>
  );
}

function DealDetail({ d, onBack, onCommit, onOpenMerchantConv, onAddPost, onDelPost, busy }) {
  const { deal, terms: t, blogger: b, checks, activity, conversations: c, posts } = d;
  const dt = deal.terms || {};
  const warns = (checks || []).filter((x) => x.level === 'warn');
  const oks = (checks || []).filter((x) => x.level !== 'warn');

  return (
    <div style={st.body}>
      <div style={st.actionRow}>
        <GhostBtn small onClick={onBack}>← 返回</GhostBtn>
        {deal.status === 'agreed' && (
          <PrimaryBtn small disabled={busy} onClick={onCommit}>
            {busy ? '落库中…' : '落成报名记录'}
          </PrimaryBtn>
        )}
      </div>

      {/* 抬头：谁、跟哪家店、什么状态，一行说完 */}
      <div style={st.headCard}>
        <div style={st.cardTop}>
          {b.avatar && <img src={b.avatar} alt="" style={st.avatar} />}
          <span style={st.headName}>{b.name || deal.bloggerPhone}</span>
          <span style={{ ...st.status, ...(DEAL_TONE[deal.status] || {}) }}>{DEAL_LABEL[deal.status] || deal.status}</span>
          {deal.score > 0 && <span style={st.score}>{deal.score} 分</span>}
          {deal.origin === 'self' && <span style={st.chip}>自己来接的</span>}
          {deal.merchantObjectedAt && <span style={st.chipNo}>商家有异议</span>}
        </div>
        <div style={st.headSub}>
          {deal.merchantName}{activity?.name ? ` · ${activity.name}` : ''}
          {b.xhsAccount ? ` · ${b.xhsAccount}` : ''} · {b.fans} 粉
          {b.er > 0 ? ` · 互动 ${b.er}%` : ''}
        </div>
      </div>

      {/* 体检：要看的排前面。全绿也留着 —— 让运营知道这几项查过了 */}
      <div style={st.card}>
        <div style={st.panelTitle}>这单对不对</div>
        {(checks || []).length === 0 && <div style={st.reason}>商家还没填条件，没得比。</div>}
        {[...warns, ...oks].map((x) => (
          <div key={x.key} style={st.checkRow}>
            <span style={x.level === 'warn' ? st.badgeWarn : st.badgeOk}>{x.level === 'warn' ? '要看' : '没问题'}</span>
            <span style={st.checkLabel}>{x.label}</span>
            <span style={st.checkDetail}>{x.detail}</span>
          </div>
        ))}
      </div>

      {/* 发布证据。运营在这一步要做的就两件事：核对他发的是不是那篇、
          把链接连同一段话给商家。所以链接、截图、还差哪个平台、能直接复制的
          那段话，都摆在一块儿，不用再去别处翻。 */}
      {posts && (posts.required > 0) && (
        <div style={st.card}>
          <div style={st.cardTop}>
            <span style={st.panelTitle}>发布证据</span>
            <span style={posts.allFilled ? st.badgeOk : st.badgeWarn}>
              {posts.filled}/{posts.required} {posts.allFilled ? '已交齐' : '还差'}
            </span>
            {b.xhsUrl && (
              <a style={st.linkBtn} href={b.xhsUrl} target="_blank" rel="noreferrer">打开他的小红书主页</a>
            )}
            {(b.otherPlatforms || []).filter((x) => x.url).map((x) => (
              <a key={x.type + x.url} style={st.linkBtn} href={x.url} target="_blank" rel="noreferrer">
                打开他的 {x.type} 主页
              </a>
            ))}
          </div>

          {posts.items.length === 0 && (
            <div style={st.reason}>他还没回填。稿子过了 6 小时会自动催一次，再 48 小时催第二次，之后转到这儿让人工跟。</div>
          )}

          {posts.items.map((x) => (
            <div key={x.idx} style={st.postRow}>
              <div style={st.cardTop}>
                <span style={st.name}>{x.name}</span>
                {!x.url && <span style={st.sub}>（只交了截图）</span>}
                {x.url && <a style={st.linkBtn} href={x.url} target="_blank" rel="noreferrer">打开帖子</a>}
                {/* 截图核对只是提示，核不过也没拦他发 —— 这里让运营自己看一眼判断 */}
                {x.shotVerdict && x.shotVerdict !== 'pass' && (
                  <span style={st.chipNo} title={x.shotReason}>截图核对：{x.shotVerdict === 'fail' ? '没通过' : '拿不准'}</span>
                )}
                {x.shotVerdict === 'pass' && <span style={st.badgeOk}>截图核对通过</span>}
                <span style={st.sub}>{x.filledAt ? new Date(x.filledAt).toLocaleString('zh-CN') : ''}</span>
                {x.byOps && <span style={st.sub}>（{x.byOps} 代填）</span>}
                {x.idx >= 0 && (
                  <span style={st.delLink} onClick={() => {
                    if (window.confirm(`删掉「${x.name}」这一条？`)) onDelPost(x.idx);
                  }}>删掉</span>
                )}
              </div>
              {x.url && <div style={st.postUrl}>{x.url}</div>}
              {x.shotReason && <div style={st.sub}>{x.shotReason}</div>}
              {(x.shots || []).length > 0 && (
                <div style={st.shots}>
                  {x.shots.map((u) => (
                    <a key={u} href={u} target="_blank" rel="noreferrer">
                      <img src={u} alt="" style={st.shot} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* 运营代填。博主把链接甩微信里、或者就是不爱开 App —— 替他贴一下比一直催有用 */}
          <div style={{ ...st.actionRow, marginTop: 10, marginBottom: 0 }}>
            <GhostBtn small onClick={() => {
              const platform = window.prompt('发在哪个平台？（小红书 / Instagram / 抖音 …）', '小红书');
              if (platform === null) return;
              const url = window.prompt('把帖子链接贴进来：', '');
              if (!url || !url.trim()) return;
              onAddPost(platform.trim(), url.trim());
            }}>替他贴一条链接</GhostBtn>
          </div>

          {posts.copyLine && (
            <div style={{ ...st.actionRow, marginTop: 10, marginBottom: 0 }}>
              <GhostBtn small onClick={() => {
                navigator.clipboard?.writeText(posts.copyLine)
                  .then(() => window.alert('复制好了，粘到商家那条会话里发过去就行'))
                  .catch(() => window.prompt('手动复制：', posts.copyLine));
              }}>复制给商家的话</GhostBtn>
              <span style={st.sub}>（商家端本来就能看到，这段是想额外说一声时用）</span>
            </div>
          )}
        </div>
      )}

      {/* 两边并排。以前一次只能看一边，运营核对「博主说的时间跟商家给的档位对不对」
          要来回切页面，切回来还得重新找位置 —— 这是运营自己提的 */}
      <div style={st.twoPane}>
        <ConvPane
          conversationId={c.blogger}
          who="博主"
          title={`博主 · ${b.name || deal.bloggerPhone}`}
          sub={b.xhsAccount ? `${b.xhsAccount} · ${b.fans} 粉` : deal.bloggerPhone}
          needsOps={c.bloggerNeedsOps}
          height={380}
        />
        <ConvPane
          conversationId={c.merchant}
          who="商家"
          // 单子上的商家名是下单那一刻的快照，早期数据和演练脚本留下的孤儿单会是空的。
          // 空着比写错更难认 —— 退回活动上的名字，再不行就把 id 摆出来
          title={`商家 · ${deal.merchantName || activity?.merchantName || `（没记店名 · ${deal.merchantId || '连 id 都没有'}）`}`}
          sub="这条是「一店一条」的会话，里面混着这家店所有单子的消息"
          needsOps={c.merchantNeedsOps}
          height={380}
          onCreate={onOpenMerchantConv}
        />
      </div>

      <div style={st.grid}>
        <Panel title="商家要什么" sub={t ? '' : '还没填'}>
          {!t ? <div style={st.reason}>这家店还没配探店条件，助理谈判时缺依据。</div> : (
            <>
              <Row k="套餐价值" v={t.mealValue ? `$${t.mealValue}${t.maxMealValue ? `（最多让到 $${t.maxMealValue}）` : ''}` : ''} />
              <Row k="现金" v={t.allowPaid ? `可谈，最多 $${t.maxPaidFee || 0}` : '纯置换，不谈钱'} />
              <Row k="粉丝门槛" v={t.minFollowers ? `${t.minFollowers} 粉以上` : ''} />
              <Row k="这轮招" v={t.neededCount ? `${t.neededCount} 位` : ''} />
              <Row k="到店人数" v={t.peopleAllowed ? `最多 ${t.peopleAllowed} 人（含本人）` : ''} />
              <Row k="可接待" v={fmtSlots(t.availability)} />
              <Row k="不接待" v={fmtList(t.blackoutDates)} />
              <Row k="同时段最多" v={t.maxPerSlot ? `${t.maxPerSlot} 位` : ''} />
              <Row k="发布" v={`${t.postCount || 1} 篇${(t.platforms || []).length ? ` · ${t.platforms.join('、')}` : ''}${t.deadlineDays ? ` · 到店后 ${t.deadlineDays} 天内` : ''}`} />
              <Row k="必带话题" v={fmtList(t.mustTags)} />
              <Row k="必拍" v={fmtList(t.mustShoot)} />
              <Row k="不许出现" v={fmtList(t.bannedWords)} />
              <Row k="硬指标" v={`字数 ${t.minWords || 0} · 图 ${t.minImages || 0} 张 · 话题 ${t.minTags || 0} 个`} />
              <Row k="Google 评价" v={t.needGoogleReview ? '要' : ''} />
              <Row k="发前看稿" v={t.requireDraftReview ? '要' : ''} />
              <Row k="地址" v={t.storeAddress} />
              <Row k="想突出" v={t.highlights} />
              <Row k="其他要求" v={t.notes} />
            </>
          )}
        </Panel>

        <Panel title="这单谈的" sub={DEAL_LABEL[deal.status] || ''}>
          <Row k="到店时间" v={fmtTime(dt.visitAt) || '还没定'} />
          <Row k="去哪家店" v={dt.storeAddress} />
          <Row k="几个人" v={`${dt.peopleCount || 1} 人`} />
          <Row k="套餐" v={dt.mealValue ? `$${dt.mealValue}` : ''} />
          <Row k="现金" v={dt.paidFee ? `$${dt.paidFee}` : ''} />
          <Row k="发布" v={`${dt.postCount || 1} 篇${(dt.platforms || []).length ? ` · ${dt.platforms.join('、')}` : ''}`} />
          <Row k="备注" v={dt.note} />
          {dt.content && (
            <>
              <div style={st.panelSep}>内容要求（发邀请那天拓的，按这份算）</div>
              <Row k="必带话题" v={fmtList(dt.content.mustTags)} />
              <Row k="必拍" v={fmtList(dt.content.mustShoot)} />
              <Row k="硬指标" v={`字数 ${dt.content.minWords || 0} · 图 ${dt.content.minImages || 0} 张 · 话题 ${dt.content.minTags || 0} 个`} />
              <Row k="Google 评价" v={dt.content.needGoogleReview ? '要' : ''} />
            </>
          )}
          <Row k="商家确认" v={deal.merchantApproved ? '已确认' : (deal.merchantObjectedAt ? `有异议：${deal.merchantObjection || '没说原因'}` : '')} />
          {/* 商家事后打的分。三项都是客观的（准时/完成度/配合度），分数会进博主画像
              影响以后推不推他；但自由评语只留在这儿给运营看，不进画像 ——
              万一商家写的是「这次夸得不够多」，那句话不该出现在助理跟博主的对话上下文里 */}
          <Row k="商家打分" v={deal.ratedAt
            ? `准时 ${deal.rating?.onTime || 0} · 完成度 ${deal.rating?.content || 0} · 配合 ${deal.rating?.cooperation || 0}`
            : ''} />
          <Row k="商家评语" v={deal.merchantComment} />
          <Row k="已落库" v={deal.committedApplicationId ? '是' : ''} />
        </Panel>

        <Panel title="博主是谁">
          <Row k="电话" v={deal.bloggerPhone} />
          <Row k="小红书" v={b.xhsUrl
            ? <a style={st.link} href={b.xhsUrl} target="_blank" rel="noreferrer">{b.xhsAccount || '打开主页'}</a>
            : b.xhsAccount} />
          <Row k="粉丝" v={`${b.fans}${b.fansSelf && b.fansSelf !== b.fans ? `（自填 ${b.fansSelf}）` : ''}`} />
          <Row k="互动率" v={b.er ? `${b.er}%` : ''} />
          <Row k="平台标签" v={fmtList(b.tags)} />
          {(b.otherPlatforms || []).filter((x) => x.account).map((x) => (
            <Row key={x.type + x.account} k={x.type || '其他平台'}
              v={x.url ? <a style={st.link} href={x.url} target="_blank" rel="noreferrer">{x.account}（{x.followers} 粉）</a>
                : `${x.account}（${x.followers} 粉）`} />
          ))}
          <Row k="沟通语言" v={b.lang === 'en' ? '英文' : ''} />
          <Row k="常去" v={fmtList(b.areas)} />
          <Row k="通勤" v={b.maxTravelMinutes ? `愿意跑 ${b.maxTravelMinutes} 分钟${b.hasCar ? '，有车' : ''}` : (b.hasCar ? '有车' : '')} />
          <Row k="口味" v={fmtList(b.cuisines)} />
          <Row k="忌口" v={fmtList(b.dislikes)} />
          <Row k="粉丝画像" v={b.audienceNote} />
          <Row k="配合度" v={b.cooperation} />
          <Row k="简介" v={b.bio} />
          <Row k="运营备注" v={b.note} />
          {(b.learned || []).length > 0 && (
            <>
              <div style={st.panelSep}>助理聊出来的</div>
              {b.learned.map((x, i) => <div key={i} style={st.reason}>· {x}</div>)}
            </>
          )}
        </Panel>
      </div>

      {/* 当时为什么推他 —— 判断「匹配准不准」看的就是这几行跟上面那个人对不对得上 */}
      <div style={st.card}>
        <div style={st.panelTitle}>当时为什么推他</div>
        {(deal.reasons || []).length === 0
          ? <div style={st.reason}>没记下理由（博主自己来接的，或早于这个字段的老单子）</div>
          : deal.reasons.map((r, i) => <div key={i} style={st.reason}>· {r}</div>)}
        {(deal.gaps || []).length > 0 && <div style={st.gaps}>邀请时还缺：{deal.gaps.join('、')}</div>}
      </div>
    </div>
  );
}

// 翻页条。线上一个来回 1.5 秒（新加坡中转那一跳），所以一页只取 20 条 ——
// 与其等一次把几百条拉完，不如让翻页这件事本身快。
// 只有一页时不显示，免得页面上多一行没用的东西。
function Pager({ page, total, pageSize = 20, onPage }) {
  const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (pages <= 1) return null;
  const go = (n) => { onPage(Math.min(pages, Math.max(1, n))); window.scrollTo(0, 0); };
  return (
    <div style={st.pager}>
      <GhostBtn small disabled={page <= 1} onClick={() => go(page - 1)}>上一页</GhostBtn>
      <span style={st.sub}>第 {page} / {pages} 页 · 共 {total} 条</span>
      <GhostBtn small disabled={page >= pages} onClick={() => go(page + 1)}>下一页</GhostBtn>
    </div>
  );
}

// 一行撮合单。跨店总表和单店里用的是同一张，总表多显示一个店名
//
// 「匹配得对不对」是这一版最要紧的判断，所以这行不光说「推了谁」，
// 还能就地摊开「他是个什么人、当时凭什么推他」——
// 演练时不用记着名字再切到「博主」那一栏去翻。
function DealRow({ d, onOpen, onChat, onMerchantChat, onCommit }) {
  const b = d.blogger;
  return (
    <div style={st.card}>
      <div style={st.cardTop}>
        {d.score > 0 && <span style={st.score}>{d.score}</span>}
        <span style={st.name}>{d.bloggerName || d.bloggerPhone}</span>
        <span style={{ ...st.status, ...(DEAL_TONE[d.status] || {}) }}>{DEAL_LABEL[d.status] || d.status}</span>
        <span style={st.sub}>{d.merchantName}</span>
        {b && <span style={st.sub}>{b.xhsAccount || '没填小红书号'} · {b.fans} 粉</span>}
        {d.origin === 'self' && <span style={st.chip}>自己来接的</span>}
        {d.merchantObjectedAt && <span style={st.chipNo}>商家有异议</span>}
      </div>
      <div style={st.terms}>
        时间 {d.terms?.visitAt ? new Date(d.terms.visitAt).toLocaleString('zh-CN') : '未定'} ·
        {' '}{d.terms?.peopleCount || 1} 人 ·
        套餐 ${d.terms?.mealValue || 0}
        {d.terms?.paidFee ? ` · 现金 $${d.terms.paidFee}` : ''}
        {d.terms?.note ? ` · ${d.terms.note}` : ''}
      </div>
      <div style={st.actions}>
        <GhostBtn small onClick={onOpen}>看这单</GhostBtn>
        {d.conversationId && <GhostBtn small onClick={onChat}>博主那边</GhostBtn>}
        {d.merchantConversationId && <GhostBtn small onClick={onMerchantChat}>商家那边</GhostBtn>}
        {d.status === 'agreed' && <PrimaryBtn small onClick={onCommit}>落成报名记录</PrimaryBtn>}
        {d.committedApplicationId && <span style={st.sub}>已落库</span>}
      </div>
    </div>
  );
}

// 一个博主的画像
function BloggerCard({ p, setMsg, openChat, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={st.card}>
      <div style={st.cardTop}>
        <span style={st.score}>{p.completeness}%</span>
        <span style={st.name}>{p.name || p.phone}</span>
        {/* 粉丝数以平台抓到的为准，自填的只在对不上时才显示出来对照 */}
        <span style={st.sub}>{p.real ? p.real.fans : p.xhsFollowers} 粉</span>
        {p.real?.er > 0 && <span style={st.sub}>互动 {p.real.er}%</span>}
        {p.real?.ageDays > 30 && <span style={st.sub}>数据 {p.real.ageDays} 天前</span>}
        {!p.real && <span style={st.chipNo}>没抓到平台数据</span>}
      </div>
      {p.mismatch && (
        <div style={st.gaps}>
          自填 {p.mismatch.self} 粉，平台显示 {p.mismatch.real} 粉。
          多半是入驻时填完又涨了，或者把几个平台加在一起了 —— 看一眼就行，别去问他。
        </div>
      )}
      {p.known.length > 0 && (
        <div style={st.knownRow}>
          {p.areas.length > 0 && <span style={st.chip}>{p.areas.join('/')}</span>}
          {p.cuisines.length > 0 && <span style={st.chip}>爱吃 {p.cuisines.join('/')}</span>}
          {p.dislikes.length > 0 && <span style={st.chipNo}>忌 {p.dislikes.join('/')}</span>}
          {p.audienceNote && <span style={st.chip}>{p.audienceNote}</span>}
        </div>
      )}
      {p.missing.length > 0 && <div style={st.gaps}>还缺：{p.missing.join('、')}</div>}
      {(p.learned || []).slice(-3).map((l, i) => <div key={i} style={st.reason}>· {l}</div>)}
      <div style={st.actions}>
        <GhostBtn small onClick={() => setOpen(!open)}>{open ? '收起画像' : '看 / 改画像'}</GhostBtn>
      </div>
      {open && <BloggerPortrait phone={p.phone} name={p.name || p.phone} setMsg={setMsg} openChat={openChat}
        onChanged={onChanged} />}
    </div>
  );
}

// 完整画像 + 每一次变动（2026-10-03 用户）：
// 运营看得到系统最后生成的画像，每一项都能改、能加；改动全留记录，改错了能恢复。
// 运营碰得到的只有画像内容，打分规则本身不开放。
const SCORED = ['areas', 'cuisines', 'dislikes', 'maxTravelMinutes', 'hasCar', 'wantsPaid', 'minMealValue'];
const EV_STATUS = { active: '生效中', pending: '待审', reverted: '已撤销', rejected: '没要' };
function BloggerPortrait({ phone, name, setMsg, openChat, onChanged }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    agentApi.signals(phone).then(setD).catch((e) => setMsg(e?.response?.data?.error || '画像没拉到'));
  }, [phone, setMsg]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div style={st.reason}>加载中…</div>;
  const sig = d.signals || {};

  const save = (field, value, ok = '改好了') => agentApi.signalEdit(phone, field, value)
    .then(() => { setMsg(ok); load(); onChanged && onChanged(); })
    .catch((e) => setMsg(e?.response?.data?.error || '没改成'));
  const list = (f) => (Array.isArray(sig[f]) ? sig[f] : []);
  const addTo = (f) => {
    const v = window.prompt(`加到「${SIG_FIELD[f]}」里（几个一起加用顿号隔开）：`);
    if (v && v.trim()) save(f, [...list(f), ...v.split(/[、,，]+/)], '加上了');
  };
  const editList = (f) => {
    const v = window.prompt(`「${SIG_FIELD[f]}」改成（用顿号隔开，清空就是全删）：`, list(f).join('、'));
    if (v !== null) save(f, v);
  };
  const editNum = (f, unit) => {
    const v = window.prompt(`「${SIG_FIELD[f]}」改成多少${unit}？`, sig[f] || '');
    if (v !== null && v.trim() !== '') save(f, v.trim());
  };
  const editText = (f) => {
    const v = window.prompt(`「${SIG_FIELD[f]}」改成：`, sig[f] || '');
    if (v !== null) save(f, v);
  };
  const editLearned = (i) => {
    const cur = list('learned');
    const v = window.prompt('这一条改成（清空就是删掉）：', cur[i]);
    if (v === null) return;
    const next = cur.slice(); if (v.trim()) next[i] = v.trim(); else next.splice(i, 1);
    save('learned', next);
  };
  const revert = (e) => {
    const what = e.source === 'ops' ? '恢复成这次改之前的样子' : '把这条从画像里撤掉';
    if (!window.confirm(`${what}？`)) return;
    agentApi.judgeSignal(e._id, 'revert', '', undefined)
      .then(() => { setMsg('恢复了'); load(); onChanged && onChanged(); })
      .catch((err) => setMsg(err?.response?.data?.error || '没恢复成'));
  };
  const tag = (f) => <span style={st.sub}>{SCORED.includes(f) ? '影响打分' : '给助理看，不影响打分'}</span>;
  // 一项一行：名字 · 现在的内容 · 按钮
  const Row = ({ f, children, btns }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: '1px solid #f5f5f5' }}>
      <div style={{ width: 120, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{SIG_FIELD[f]}</div>{tag(f)}
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', minHeight: 26 }}>{children}</div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>{btns}</div>
    </div>
  );

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
      {['areas', 'cuisines', 'dislikes'].map((f) => (
        <Row key={f} f={f} btns={<>
          <GhostBtn small onClick={() => addTo(f)}>加</GhostBtn>
          <GhostBtn small onClick={() => editList(f)}>改</GhostBtn>
        </>}>
          {list(f).length ? list(f).map((x) => <span key={x} style={f === 'dislikes' ? st.chipNo : st.chip}>{x}</span>)
            : <span style={st.sub}>没有</span>}
        </Row>
      ))}
      <Row f="maxTravelMinutes" btns={<GhostBtn small onClick={() => editNum('maxTravelMinutes', '分钟')}>改</GhostBtn>}>
        <span style={st.sub}>{sig.maxTravelMinutes ? `${sig.maxTravelMinutes} 分钟` : '没填'}</span>
      </Row>
      <Row f="minMealValue" btns={<GhostBtn small onClick={() => editNum('minMealValue', '澳元')}>改</GhostBtn>}>
        <span style={st.sub}>{sig.minMealValue ? `$${sig.minMealValue}` : '没填'}</span>
      </Row>
      <Row f="hasCar" btns={<GhostBtn small onClick={() => save('hasCar', !sig.hasCar)}>改成「{sig.hasCar ? '没有' : '有'}」</GhostBtn>}>
        <span style={st.sub}>{sig.hasCar ? '有车' : '没车 / 不清楚'}</span>
      </Row>
      <Row f="wantsPaid" btns={<GhostBtn small onClick={() => save('wantsPaid', !sig.wantsPaid)}>改成「{sig.wantsPaid ? '不要' : '要'}」</GhostBtn>}>
        <span style={st.sub}>{sig.wantsPaid ? '要现金' : '不要 / 不清楚'}</span>
      </Row>
      <Row f="audienceNote" btns={<GhostBtn small onClick={() => editText('audienceNote')}>改</GhostBtn>}>
        <span style={st.sub}>{sig.audienceNote || '没有'}</span>
      </Row>
      <Row f="learned" btns={<GhostBtn small onClick={() => {
        const v = window.prompt('加一条补充说明（比如「住 Box Hill，周末才有空」）：');
        if (v && v.trim()) save('learned', [...list('learned'), v.trim()], '加上了');
      }}>加一条</GhostBtn>}>
        <div style={{ width: '100%' }}>
          {list('learned').length ? list('learned').map((l, i) => (
            <div key={i} style={st.reason}>· {l} <span style={{ ...st.sub, cursor: 'pointer', color: '#FA1C51' }} onClick={() => editLearned(i)}>改</span></div>
          )) : <span style={st.sub}>没有</span>}
        </div>
      </Row>

      <div style={{ ...st.name, marginTop: 12 }}>变动记录</div>
      {!(d.events || []).length ? <div style={st.reason}>还没有变动。</div> : d.events.map((e) => (
        <div key={e._id} style={st.sigRow}>
          <div style={st.cardTop}>
            <span style={e.source === 'ops' ? st.badgeWarn : st.badgeOk}>{e.source === 'ops' ? `运营 ${e.reviewedBy || ''}` : '助理'}</span>
            <span style={st.sub}>{SIG_FIELD[e.field] || e.field}</span>
            <span style={st.sub}>{EV_STATUS[e.status] || e.status}</span>
            <span style={st.sub}>{timeAgo(e.createdAt)}</span>
          </div>
          <div style={st.reason}>{e.source === 'ops' ? '改成：' : ''}{e.value || '（清空）'}</div>
          <div style={st.actions}>
            {e.status === 'active' && <GhostBtn small onClick={() => revert(e)}>{e.source === 'ops' ? '恢复成改之前' : '撤掉'}</GhostBtn>}
            {e.status === 'pending' && <span style={st.sub}>在上面「画像待审」里处理</span>}
            {e.conversationId && <GhostBtn small onClick={() => openChat(e.conversationId, 'bloggers', name)}>看当时的对话</GhostBtn>}
          </div>
        </div>
      ))}
    </div>
  );
}


// 一份待审稿件。机器能查的都查过了，这里只呈现人要判断的东西。
function DraftCard({ d, onDone, setMsg, askWhy }) {
  const [open, setOpen] = useState(false);
  const [shot, setShot] = useState(-1);   // 看大图，-1 = 没开
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const soft = (d.issues || []).filter((x) => x.soft);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); setMsg(ok); onDone(); }
    catch (e) { setMsg(e?.response?.data?.error || '没成功'); }
    finally { setBusy(false); }
  };

  const manual = d.status === 'ops_review';
  const reject = () => {
    // 打回必须写清改哪里，不然博主收到一句「不合格」无从下手
    const reason = window.prompt('让博主改哪里？这句话会原样发给他（商家的意见可以直接贴进来）：');
    if (!reason || !reason.trim()) return;
    // 人工审稿打回是正常流程，不是助理做错了什么，不用选原因
    if (manual) { act(() => agentApi.rejectDraft(d._id, reason.trim(), 'unsure'), '已打回，博主收到了'); return; }
    askWhy('这篇稿子哪儿不对？').then((tag) => {
      if (!tag) return;
      act(() => agentApi.rejectDraft(d._id, reason.trim(), tag), '已打回，博主收到了');
    });
  };

  // 建议会原样发给博主，所以措辞是「下次可以试试」而不是「不合格」——
  // 稿子已经发出去了，现在说的是下一次
  const advise = () => {
    const advice = window.prompt('下次可以怎么写得更好？这句话会原样发给他：');
    if (!advice || !advice.trim()) return;
    act(() => agentApi.reviewedDraft(d._id, rating, advice.trim()), '记下了，已发给博主');
  };

  const body = d.text || '';
  const short = body.length > 160 && !open ? body.slice(0, 160) + '…' : body;

  return (
    <div style={st.card}>
      <div style={st.cardTop}>
        <span style={st.name}>{d.bloggerName || d.bloggerPhone}</span>
        <span style={st.sub}>{d.merchantName}</span>
        <span style={st.sub}>{d.platform === 'instagram' ? 'Instagram' : '小红书'}</span>
        <span style={st.sub}>{d.images?.length || 0} 图{(d.videos || []).length ? ` · ${d.videos.length} 视频` : ''}</span>
        {d.round > 1 && <span style={st.chipNo}>第 {d.round} 稿</span>}
      </div>

      {soft.length > 0 && (
        <div style={st.gaps}>
          机器拿不准，请重点看：{soft.map((x) => `${x.label}（${x.detail}）`).join('；')}
        </div>
      )}

      <div style={{ ...st.reason, whiteSpace: 'pre-wrap', color: '#374151', marginTop: 6 }}>{short}</div>
      {body.length > 160 && (
        <span style={{ ...st.sub, cursor: 'pointer', color: '#FA1C51' }} onClick={() => setOpen(!open)}>
          {open ? '收起' : '看全文'}
        </span>
      )}

      {/* 图得摆出来。这一栏原来只写「8 图」三个字 —— 而机器能查的本来就只有张数，
          「拍得怎么样、有没有食欲、出镜和门头齐不齐」全靠运营这双眼睛，
          看不到图这一整步就是空转。点开看大图，左右能翻。 */}
      {(d.images || []).length > 0 && (
        <div style={st.shots}>
          {d.images.map((u, i) => (
            <img key={u} src={u} alt="" style={st.shot} onClick={() => setShot(i)} />
          ))}
        </div>
      )}
      {shot >= 0 && (
        <Lightbox images={d.images} index={shot} onIndex={setShot} onClose={() => setShot(-1)} />
      )}
      {/* 视频机器不看，只能人看（2026-10-01 会议） */}
      {(d.videos || []).length > 0 && (
        <div style={st.shots}>
          {d.videos.map((u) => <video key={u} src={u} controls style={{ width: 220, maxHeight: 320, borderRadius: 8, background: '#000' }} />)}
        </div>
      )}

      {manual && (
        <div style={{ ...st.actionRow, marginTop: 10, marginBottom: 0 }}>
          <PrimaryBtn small disabled={busy}
            onClick={() => act(() => agentApi.approveDraft(d._id, ''), '已通过，博主收到了「可以发了」')}>
            通过，可以发了
          </PrimaryBtn>
          <GhostBtn small disabled={busy} onClick={reject}>打回，写修改意见</GhostBtn>
          {/* 沿用老后台：发一个审稿链接，商家在网页上看稿、通过或退回（退回的意见会发给博主） */}
          {d.shareToken && <GhostBtn small onClick={() => {
            const u = tshareUrl(d.shareToken, { view: 'drafts', app: d.dealId });
            navigator.clipboard.writeText(u).then(() => setMsg('审稿链接已复制，发给商家')).catch(() => window.prompt('复制下面这个链接', u));
          }}>复制审稿链接给商家</GhostBtn>}
          <GhostBtn small onClick={() => {
            const t = `${d.bloggerName || '博主'} 的稿子（${d.merchantName}）\n\n${d.title ? d.title + '\n' : ''}${d.text || ''}`
              + `${(d.images || []).length ? '\n\n图片：\n' + d.images.join('\n') : ''}`
              + `${(d.videos || []).length ? '\n\n视频：\n' + d.videos.join('\n') : ''}`;
            navigator.clipboard.writeText(t).then(() => setMsg('稿子已复制，发给商家看')).catch(() => window.prompt('复制下面这段', t));
          }}>复制稿子文字</GhostBtn>
        </div>
      )}

      {/* 2026-09-18 起这一步是**事后回看**，不是放行 —— 博主早就能发了。
          所以主动作是「看过了」而不是「通过」，按钮上写清楚，
          免得运营以为不点博主就发不了、于是一直守着这一栏。

          打分是选填的：强制打分只会逼出一堆随手点的 4 分，
          那种数据进了画像比没有更糟。 */}
      {!manual && <div style={{ ...st.actionRow, marginTop: 10, marginBottom: 0, flexWrap: 'wrap' }}>
        <span style={st.sub}>写得怎么样</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            style={rating === n ? st.rateOn : st.rate}
            onClick={() => setRating(rating === n ? 0 : n)}
          >
            {n}
          </span>
        ))}
        <PrimaryBtn small disabled={busy}
          onClick={() => act(() => agentApi.reviewedDraft(d._id, rating, ''), '记下了')}>
          看过了
        </PrimaryBtn>
        <GhostBtn small disabled={busy} onClick={advise}>留个建议</GhostBtn>
      </div>}
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', background: '#f7f7f8' },
  todoTag: { color: '#fff', fontSize: 12, borderRadius: 999, padding: '2px 10px', marginRight: 8, whiteSpace: 'nowrap' },
  todoAt: { fontSize: 12, color: '#9ca3af' },
  todoLast: { fontSize: 13, color: '#52525b', background: '#f4f4f5', borderRadius: 6, padding: '6px 10px', marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  todoBottom: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, gap: 8 },
  handled: { fontSize: 12, color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 999, padding: '3px 12px', cursor: 'pointer' },
  // 打分：1-5 一排。不用星星 —— 星星看着像在给餐厅评价，
  // 而这是给稿子质量打的分，数字更直接
  rate: { fontSize: 13, color: '#6b7280', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, width: 30, height: 30, lineHeight: '28px', textAlign: 'center', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' },
  rateOn: { fontSize: 13, color: '#fff', border: '1px solid #FA1C51', background: '#FA1C51', borderRadius: 8, width: 30, height: 30, lineHeight: '28px', textAlign: 'center', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' },
  chipRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  filterChip: { fontSize: 13, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '4px 14px', cursor: 'pointer' },
  filterChipOn: { color: '#fff', background: '#FA1C51', borderColor: '#FA1C51' },
  shopName: { fontSize: 15, fontWeight: 600, color: '#111' },
  progWrap: { marginTop: 8 },
  progText: { fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 },
  progLive: { fontSize: 11, color: '#16a34a', background: '#f0fdf4', padding: '1px 7px', borderRadius: 4 },
  progBar: { display: 'flex', height: 6, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden', marginTop: 6 },
  progDone: { background: '#16a34a' },
  progBooked: { background: '#86efac' },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: '#374151', margin: '22px 0 10px' },
  drill: { background: '#fff7ed', borderBottom: '1px solid #fed7aa', color: '#9a3412', fontSize: 13, padding: '10px 16px', lineHeight: 1.9 },
  drillItem: { marginLeft: 10 },
  drillTail: { display: 'block', color: '#c2724a', fontSize: 12 },
  shots: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  shot: { width: 88, height: 88, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'zoom-in' },
  // ── 单子详情 ──
  // 三栏并排：商家要什么 / 这单谈的 / 博主是谁。窄屏自动堆成一列，
  // 不做断点判断 —— auto-fit 比写一堆 media query 稳
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 10, alignItems: 'start' },
  panel: { background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #f0f0f0' },
  panelTop: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 },
  panelTitle: { fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 8 },
  panelSep: { fontSize: 11, color: '#9ca3af', margin: '10px 0 4px', paddingTop: 8, borderTop: '1px solid #f5f5f5' },
  dRow: { display: 'flex', gap: 10, padding: '4px 0', alignItems: 'flex-start' },
  dK: { fontSize: 12, color: '#9ca3af', width: 66, flexShrink: 0 },
  dV: { fontSize: 12, color: '#374151', lineHeight: 1.6, wordBreak: 'break-word', flex: 1 },
  headCard: { background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid #f0f0f0' },
  headName: { fontSize: 17, fontWeight: 700, color: '#111' },
  headSub: { fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.6 },
  checkRow: { display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', flexWrap: 'wrap' },
  checkLabel: { fontSize: 12, fontWeight: 600, color: '#374151', width: 58, flexShrink: 0 },
  checkDetail: { fontSize: 12, color: '#4b5563', lineHeight: 1.6, flex: 1, minWidth: 180 },
  badgeOk: { fontSize: 11, color: '#16a34a', background: '#f0fdf4', padding: '1px 7px', borderRadius: 4, flexShrink: 0 },
  badgeWarn: { fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fffbeb', padding: '1px 7px', borderRadius: 4, flexShrink: 0 },
  termsStrip: { background: '#fff', border: '1px solid #f0f0f0', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#4b5563', lineHeight: 1.8 },
  termsK: { fontSize: 12, color: '#9ca3af', marginRight: 8 },
  sigBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  sigBadge: { fontSize: 13, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '4px 14px', cursor: 'pointer' },
  sigBadgeOn: { fontSize: 13, color: '#fff', background: '#FA1C51', border: '1px solid #FA1C51', borderRadius: 999, padding: '4px 14px', cursor: 'pointer' },
  sigLink: { fontSize: 13, color: '#2563eb', cursor: 'pointer' },
  sigRow: { borderTop: '1px solid #f5f5f5', paddingTop: 10, marginTop: 10 },
  fbId: { fontSize: 12, color: '#6b7280', background: '#f3f4f6', padding: '1px 7px', borderRadius: 4, fontFamily: 'Consolas, monospace', cursor: 'pointer' },
  pager: { display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', padding: '14px 0' },
  search: { width: '100%', marginBottom: 10, padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  verdictText: { marginTop: 10, padding: '10px 12px', background: '#f8f8f9', borderRadius: 8, fontSize: 13, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap' },
  verdict: { width: '100%', marginTop: 12, padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  whyMask: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 95, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  whyBox: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 380, padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,.18)' },
  whyTitle: { fontSize: 15, fontWeight: 700, marginBottom: 12 },
  whyList: { display: 'flex', flexDirection: 'column', gap: 8 },
  whyItem: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '11px 14px', fontSize: 14, cursor: 'pointer' },
  whySkip: { textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 14, cursor: 'pointer' },
  lightbox: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' },
  lightboxImg: { maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain' },
  bar: {
    background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '10px 12px',
    display: 'flex', flexDirection: 'column', gap: 8,
    position: 'sticky', top: 56, zIndex: 10,   // 56 = AdminHeader 的高度
  },
  select: { width: '100%', padding: '9px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff' },
  tabs: { display: 'flex', gap: 16 },
  tab: { fontSize: 14, color: '#9ca3af', cursor: 'pointer', paddingBottom: 2 },
  tabOn: { color: '#FA1C51', fontWeight: 600, borderBottom: '2px solid #FA1C51' },
  msg: { background: '#fff7ed', color: '#9a3412', fontSize: 13, padding: '8px 12px', cursor: 'pointer' },
  warn: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12, padding: '10px 12px', borderRadius: 8, marginBottom: 10, lineHeight: 1.6 },
  body: { padding: 12 },
  actionRow: { display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' },
  // 两边对照。窄屏自动堆成上下 —— 运营有时候是在手机上看的
  twoPane: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 10, marginBottom: 8, alignItems: 'start' },
  paneWrap: { background: '#fff', borderRadius: 10, padding: '10px 12px 12px', border: '1px solid #f0f0f0' },
  paneHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  paneTitle: { fontSize: 14, fontWeight: 600, color: '#111' },
  paneDot: { fontSize: 12, color: '#fff', background: '#FA1C51', borderRadius: 999, padding: '2px 8px' },
  paneState: { fontSize: 12, color: '#71717a', marginLeft: 'auto' },
  paneSub: { fontSize: 12, color: '#9ca3af', margin: '2px 0 8px' },
  paneActs: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  paneTip: { fontSize: 12, color: '#3f3f46', background: '#f4f4f5', borderRadius: 6, padding: '6px 10px', marginBottom: 8, cursor: 'pointer' },
  postRow: { borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10 },
  postUrl: { fontSize: 12, color: '#71717a', wordBreak: 'break-all', marginTop: 4 },
  delLink: { fontSize: 12, color: '#9ca3af', cursor: 'pointer', textDecoration: 'underline' },
  linkBtn: { fontSize: 12, color: '#FA1C51', border: '1px solid rgba(250,28,81,.35)', borderRadius: 6, padding: '3px 10px', textDecoration: 'none', whiteSpace: 'nowrap' },
  card: { background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid #f0f0f0' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  score: { fontSize: 16, fontWeight: 700, color: '#FA1C51', minWidth: 30 },
  name: { fontSize: 15, fontWeight: 600, color: '#111' },
  sub: { fontSize: 12, color: '#9ca3af' },
  bio: { fontSize: 12, color: '#6b7280', margin: '6px 0', lineHeight: 1.6 },
  reason: { fontSize: 12, color: '#4b5563', lineHeight: 1.7 },
  gaps: { fontSize: 11, color: '#2563eb', background: '#eff6ff', borderRadius: 6, padding: '4px 8px', marginTop: 6 },
  status: { fontSize: 11, fontWeight: 600, color: '#FA1C51', background: '#fff1f4', padding: '1px 7px', borderRadius: 4 },
  approved: { fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#f0fdf4', padding: '1px 7px', borderRadius: 4 },
  terms: { fontSize: 12, color: '#4b5563', margin: '8px 0', lineHeight: 1.6 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  sumCard: { background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #f0f0f0' },
  sumNum: { fontSize: 26, fontWeight: 700, color: '#FA1C51' },
  sumLabel: { fontSize: 12, color: '#6b7280', lineHeight: 1.6 },
  knownRow: { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' },
  detail: { borderTop: '1px solid #f0f0f0', paddingTop: 10, marginBottom: 10 },
  detailTop: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  avatar: { width: 40, height: 40, borderRadius: 20, objectFit: 'cover', border: '1px solid #e5e7eb' },
  link: { display: 'block', fontSize: 12, color: '#2563eb', textDecoration: 'none', margin: '2px 0' },
  sectionTitleSm: { fontSize: 12, fontWeight: 600, color: '#374151', margin: '10px 0 4px' },
  chip: { fontSize: 11, color: '#2563eb', background: '#eff6ff', padding: '2px 8px', borderRadius: 4 },
  chipNo: { fontSize: 11, color: '#b45309', background: '#fffbeb', padding: '2px 8px', borderRadius: 4 },
};
